import { NodeSelection } from './../../selection/index';
import { NodeCutter } from './nodecutter';
import * as CONSTANT from './../base/constant';
import { detach, Browser, isNullOrUndefined as isNOU, createElement, closest } from '@syncfusion/ej2-base';
import { InsertMethods } from './insert-methods';
import { updateTextNode, nestedListCleanUp, scrollToCursor, cleanHTMLString } from './../../common/util';
/**
 * This InsertHtml class contains methods to insert HTML nodes or text into a document.
 *
 * @hidden
 * @private
 */
var InsertHtml = /** @class */ (function () {
    function InsertHtml() {
    }
    /**
     * Inserts an HTML node or text into the specified document.
     *
     * @param {Document} docElement - The document where the node should be inserted.
     * @param {Node | string} insertNode - The node or text to be inserted. Can be a DOM Node or a string representing HTML.
     * @param {Element} [editNode] - The container or editor node where the insertion will occur.
     * @param {boolean} [isExternal] - Flag indicating if the node is from an external source. Optional.
     * @param {string} [enterAction] - Represents the action taken when 'Enter' is pressed. Optional.
     * @param {EditorManager} [editorManager] - Represents the EditorManager instance. Optional.
     * @returns {void}
     * @hidden
     * @private
     */
    InsertHtml.Insert = function (docElement, insertNode, editNode, isExternal, enterAction, editorManager) {
        var insertedNode = this.prepareInsertNode(insertNode, isExternal, editNode);
        // Capture Blazor mode from the EditorManager for util functions
        this.isBlazor = !isNOU(editorManager) ? editorManager.isBlazor : false;
        var scrollHeight = !isNOU(editNode) ? editNode.scrollHeight : 0;
        var nodeSelection = new NodeSelection(editNode);
        var nodeCutter = new NodeCutter();
        var range = nodeSelection.getRange(docElement);
        //Adjusts the selection range to handle various edge cases for cursor positioning
        range = this.adjustSelectionRange(nodeSelection, docElement, editNode, range);
        var isCursor = this.isCursorAtStartPoint(range);
        var isCollapsed = range.collapsed;
        var nodes = this.getNodeCollection(range, nodeSelection, insertedNode);
        var isInsertedNodeTable = insertedNode.nodeName.toLowerCase() === 'table';
        var closestParentNode = this.findRelevantParentNode(nodes, isInsertedNodeTable, range, editNode);
        // Handle BR parent case
        if (closestParentNode && closestParentNode.nodeName === 'BR') {
            closestParentNode = closestParentNode.parentNode;
        }
        // Handling the table insertion inside list items
        if (closestParentNode && closestParentNode.nodeName === 'LI' && isInsertedNodeTable) {
            this.handleTableInListItem(range, insertedNode, closestParentNode, nodes, nodeSelection, nodeCutter, editNode);
            return;
        }
        // Handle image insertion at empty cursor position
        var isImgOnlyNode = insertedNode.nodeName !== '#text' &&
            !isNOU(insertedNode.children[0]) &&
            !isNOU(insertedNode.children[0].tagName) &&
            insertedNode.children[0].tagName === 'IMG' &&
            insertedNode.children.length === 1;
        var isEmptyCursorPosition = isCursor &&
            range.startContainer.textContent === '' &&
            range.startContainer.nodeName !== 'BR' &&
            enterAction !== 'BR';
        if (isEmptyCursorPosition && isImgOnlyNode) {
            range.startContainer.innerHTML = '';
        }
        var isPasteContentOrInsertHtml = isExternal || (!isNOU(insertedNode) &&
            !isNOU(insertedNode.classList) &&
            insertedNode.classList.contains('pasteContent'));
        var targetCells = docElement.querySelectorAll('td.e-cell-select, th.e-cell-select');
        if (targetCells && targetCells.length > 1) {
            this.clearTargetCells(targetCells);
        }
        if (isPasteContentOrInsertHtml) {
            if (editorManager &&
                editorManager.tableObj &&
                editorManager.tableObj.tablePastingObj) {
                var tablePastingObj = editorManager.tableObj.tablePastingObj;
                var insertedTable = tablePastingObj.getValidTableFromPaste(insertedNode);
                var hasSelectedTargetCells = targetCells && targetCells.length > 0;
                if (hasSelectedTargetCells && insertedTable) {
                    // Delegate to the table pasting logic
                    tablePastingObj.handleTablePaste(insertedTable, targetCells);
                    return;
                }
            }
            this.pasteInsertHTML(nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode, enterAction);
            return;
        }
        if (this.shouldInsertOutsideRange(editNode, range, isCollapsed, closestParentNode, insertedNode)) {
            this.handleContentInsertionOutsideRange(docElement, editNode, range, nodeSelection, nodeCutter, isCollapsed, closestParentNode, insertedNode, nodes, insertNode, isCursor, enterAction);
        }
        else {
            this.handleContentInsertionInsideRange(docElement, range, nodeSelection, nodeCutter, closestParentNode, insertedNode, isCursor);
        }
        // Scroll to cursor if needed for the image
        if (this.shouldScrollToCursor(editNode, scrollHeight, insertedNode)) {
            scrollToCursor(docElement, editNode);
        }
    };
    /*
    * Clears the content of all target cells by setting their innerHTML to a line break
    */
    InsertHtml.clearTargetCells = function (cells) {
        for (var i = 0; i < cells.length; i++) {
            cells[i].innerHTML = '<br>';
        }
    };
    // Prepares the node or HTML string for insertion, attaching it to a temporary container if necessary, and ensuring valid usage.
    InsertHtml.prepareInsertNode = function (insertNode, isExternal, editNode) {
        if (typeof insertNode === 'string') {
            insertNode = cleanHTMLString(insertNode, editNode);
            var divNode = createElement('div');
            divNode.innerHTML = insertNode.replace(/&(times|divide|ne)(;?)/g, '&amp;$1$2');
            return isExternal ? divNode : divNode.firstChild;
        }
        else {
            var isValidPasteContent = !isNOU(insertNode) &&
                !isNOU(insertNode.classList) &&
                insertNode.classList.contains('pasteContent');
            if (isExternal && !isValidPasteContent) {
                var divNode = createElement('div');
                divNode.appendChild(insertNode);
                return divNode;
            }
            else {
                if (Browser.userAgent.indexOf('Firefox') !== -1) {
                    this.unwrapSpansAroundBlocks(insertNode);
                }
                return insertNode;
            }
        }
    };
    //Unwraps span elements that contain block-level elements within them.
    InsertHtml.unwrapSpansAroundBlocks = function (node) {
        var _this = this;
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        var element = node;
        // Recursively process child nodes first (bottom-up traversal)
        Array.from(element.childNodes).forEach(function (child) {
            _this.unwrapSpansAroundBlocks(child);
        });
        if (element.tagName.toLowerCase() === 'span' && element.hasChildNodes()) {
            // Define a CSS selector for common block-level elements
            var blockSelectors = 'address, article, aside, blockquote, canvas, dd, div, dl, dt, ' +
                'fieldset, figcaption, figure, footer, form, h1, h2, h3, h4, h5, h6, ' +
                'header, hr, li, main, nav, noscript, ol, p, pre, section, table, tfoot, ul, video';
            // Check if there is any block-level descendant
            var hasBlockDescendant = element.querySelector(blockSelectors) !== null;
            if (hasBlockDescendant && element.parentNode) {
                while (element.firstChild) {
                    element.parentNode.insertBefore(element.firstChild, element);
                }
                element.parentNode.removeChild(element);
            }
        }
    };
    // Adjusts the selection range to handle various edge cases for cursor positioning.
    InsertHtml.adjustSelectionRange = function (nodeSelection, docElement, editNode, range) {
        // Check if this is a collapsed selection at the beginning (offset 0)
        var isCollapsedAtStart = range.startContainer === range.endContainer &&
            range.startOffset === 0 && range.startOffset === range.endOffset;
        if (!isCollapsedAtStart) {
            return range; // Early return if not a collapsed selection at start
        }
        // Apply each adjustment in based on the cursor range.
        range = this.adjustEmptyEditorSelection(nodeSelection, docElement, editNode, range);
        range = this.adjustSelectionToFirstTextNode(nodeSelection, docElement, editNode, range);
        range = this.adjustBrElementSelection(nodeSelection, docElement, range);
        return range;
    };
    // Adjusts selection when the editor is empty with a single block element.
    InsertHtml.adjustEmptyEditorSelection = function (nodeSelection, docElement, editNode, range) {
        if (range.startContainer === editNode &&
            editNode.textContent.length === 0 &&
            (editNode.children[0].tagName === 'P' ||
                editNode.children[0].tagName === 'DIV' ||
                editNode.children[0].tagName === 'BR')) {
            nodeSelection.setSelectionText(docElement, range.startContainer.children[0], range.startContainer.children[0], 0, 0);
            return nodeSelection.getRange(docElement);
        }
        return range;
    };
    // Adjusts selection to the first text node when cursor is at the start of content.
    InsertHtml.adjustSelectionToFirstTextNode = function (nodeSelection, docElement, editNode, range) {
        if (range.startContainer === editNode &&
            editNode.textContent.trim().length > 0 && editNode.childNodes[0].tagName !== 'TABLE') {
            var focusNode = this.findFirstTextNode(range.startContainer);
            if (!isNOU(focusNode)) {
                nodeSelection.setSelectionText(docElement, focusNode, focusNode, 0, 0);
                return nodeSelection.getRange(docElement);
            }
        }
        return range;
    };
    // Adjusts selection when cursor is on a BR element
    InsertHtml.adjustBrElementSelection = function (nodeSelection, docElement, range) {
        if (range.startContainer.nodeName === 'BR') {
            var currentIndex = Array.prototype.slice.call(range.startContainer.parentElement.childNodes).indexOf(range.startContainer);
            nodeSelection.setSelectionText(docElement, range.startContainer.parentElement, range.startContainer.parentElement, currentIndex, currentIndex);
            return nodeSelection.getRange(docElement);
        }
        return range;
    };
    // Handles the insertion of a table element within a list item context.
    InsertHtml.handleTableInListItem = function (range, insertedNode, closestParentNode, nodes, nodeSelection, nodeCutter, editNode) {
        if (nodes.length === 0) {
            var tableCursor = nodeSelection.processedTableImageCursor(range);
            if (tableCursor.startName === 'TABLE' || tableCursor.endName === 'TABLE') {
                var tableNode = tableCursor.start ? tableCursor.startNode : tableCursor.endNode;
                nodes.push(tableNode);
            }
        }
        var lastClosestParentNode = this.findClosestRelevantElement(nodes[nodes.length - 1].parentNode, editNode);
        this.insertTableInList(range, insertedNode, closestParentNode, nodes[0], nodeCutter, lastClosestParentNode);
    };
    // Determines if the cursor is positioned at the start of the range.
    InsertHtml.isCursorAtStartPoint = function (range) {
        return range.startOffset === 0 && range.startOffset === range.endOffset &&
            range.startContainer === range.endContainer;
    };
    // Identifies the most contextually relevant parent node for insertion based on various criteria.
    InsertHtml.findRelevantParentNode = function (nodes, isInsertedNodeTable, range, editNode) {
        if (isInsertedNodeTable) {
            return (!isNOU(nodes[0]) && !isNOU(nodes[0].parentNode)) ?
                this.findClosestRelevantElement(nodes[0].parentNode, editNode) : range.startContainer;
        }
        else {
            return nodes[0];
        }
    };
    // Checks if the content should be inserted outside the existing selection range based on multiple checks.
    InsertHtml.shouldInsertOutsideRange = function (editNode, range, isCollapsed, closestParentNode, insertedNode) {
        return editNode !== range.startContainer && ((!isCollapsed && !(closestParentNode.nodeType === Node.ELEMENT_NODE &&
            CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) !== -1))
            || (insertedNode.nodeName.toLowerCase() === 'table' && closestParentNode &&
                CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) === -1));
    };
    // Handles insertion of content outside the specified selection range, managing complex cases including tables.
    InsertHtml.handleContentInsertionOutsideRange = function (docElement, editNode, range, nodeSelection, nodeCutter, isCollapsed, closestParentNode, insertedNode, nodes, insertNode, isCursor, enterAction) {
        // Extract content and prepare for insertion
        var preNode = nodeCutter.GetSpliceNode(range, closestParentNode);
        var sibNode = preNode.previousSibling;
        var parentNode = preNode.parentNode;
        // Update selection based on node structure
        if (nodes.length === 1) {
            nodeSelection.setSelectionContents(docElement, preNode);
            range = nodeSelection.getRange(docElement);
        }
        else if (parentNode && parentNode.nodeName !== 'LI') {
            var lasNode = nodeCutter.GetSpliceNode(range, nodes[nodes.length - 1].parentElement);
            lasNode = isNOU(lasNode) ? preNode : lasNode;
            nodeSelection.setSelectionText(docElement, preNode, lasNode, 0, (lasNode.nodeType === 3) ? lasNode.textContent.length : lasNode.childNodes.length);
            range = nodeSelection.getRange(docElement);
        }
        // Extract content or clean up nested lists
        this.extractOrCleanupContent(range, parentNode);
        // Handle table insertion specially
        if (insertNode.tagName === 'TABLE') {
            this.cleanupForTableInsertion(range, editNode);
        }
        // Remove original nodes after processing
        this.removeOriginalNodes(nodes);
        // Insert node at appropriate location
        this.insertNodeAtLocation(docElement, sibNode, parentNode, editNode, insertedNode, preNode, insertNode, isCursor, range, enterAction);
        this.removeEmptyElements(editNode);
        if (insertedNode.nodeName === '#text' &&
            insertedNode.textContent.trim() === '' && insertedNode.textContent.length !== 0) {
            nodeSelection.setCursorPoint(docElement, insertedNode, insertedNode.textContent.length);
        }
        else {
            this.setSelectionAfterInsertion(insertedNode, nodeSelection, docElement);
        }
    };
    // Extracts content or cleans nested lists as required when managing inserts in outer content ranges.
    InsertHtml.extractOrCleanupContent = function (range, parentNode) {
        if (range.startContainer.parentElement.closest('ol,ul') !== null &&
            range.endContainer.parentElement.closest('ol,ul') !== null) {
            nestedListCleanUp(range, parentNode);
        }
        else {
            range.extractContents();
        }
    };
    // Performs cleanup operations necessary specifically for cases involving table insertions.
    InsertHtml.cleanupForTableInsertion = function (range, editNode) {
        var emptyElement = closest(range.startContainer, 'blockquote');
        if (!isNOU(emptyElement) && emptyElement.childNodes.length > 0) {
            for (var i = emptyElement.childNodes.length - 1; i >= 0; i--) {
                var currentChild = emptyElement.childNodes[i];
                if (!isNOU(currentChild) && currentChild.innerText.trim() === '') {
                    detach(currentChild);
                }
            }
        }
        this.removeEmptyElements(editNode, false, emptyElement);
    };
    // Removes the original nodes from the document tree after processing insertion operations.
    InsertHtml.removeOriginalNodes = function (nodes) {
        for (var index = 0; index < nodes.length; index++) {
            if (nodes[index].nodeType !== 3 && nodes[index].parentNode != null) {
                if (nodes[index].nodeName === 'IMG') {
                    continue;
                }
                nodes[index].parentNode.removeChild(nodes[index]);
            }
        }
    };
    // Directly inserts the node at a calculated location, ensuring appropriate context and order.
    InsertHtml.insertNodeAtLocation = function (docElement, sibNode, parentNode, editNode, insertedNode, preNode, insertNode, isCursor, range, enterAction) {
        if (!isNOU(sibNode) && !isNOU(sibNode.parentNode)) {
            if (docElement.contains(sibNode)) {
                InsertMethods.AppendBefore(insertedNode, sibNode, true);
            }
            else {
                range.insertNode(insertedNode);
            }
        }
        else {
            parentNode = this.findAppropriateParentNode(parentNode, editNode);
            this.insertNodeBasedOnContext(parentNode, editNode, insertedNode, insertNode, isCursor, range, preNode, enterAction);
        }
    };
    // Identifies an appropriate parent node which accommodates the insertion effectively.
    InsertHtml.findAppropriateParentNode = function (parentNode, editNode) {
        var previousNode = null;
        while (parentNode !== editNode && parentNode.firstChild &&
            (parentNode.textContent.trim() === '') && parentNode.nodeName !== 'LI') {
            var parentNode1 = parentNode.parentNode;
            previousNode = parentNode;
            parentNode = parentNode1;
        }
        return previousNode !== null ? previousNode : parentNode;
    };
    // Inserts nodes by considering established contexts like sibling nodes and nested elements.
    InsertHtml.insertNodeBasedOnContext = function (parentNode, editNode, insertedNode, insertNode, isCursor, range, preNode, enterAction) {
        if (parentNode.firstChild && (parentNode !== editNode ||
            (insertedNode.nodeName === 'TABLE' && isCursor && parentNode === range.startContainer &&
                parentNode === range.endContainer))) {
            if (parentNode.textContent.trim() === '' && parentNode !== editNode && parentNode.nodeName === 'LI') {
                parentNode.appendChild(insertedNode);
            }
            else if (parentNode.textContent.trim() === '' && parentNode !== editNode) {
                if (parentNode.parentNode && parentNode.parentNode === editNode
                    && !this.isBlockElement(insertedNode) && !(enterAction && enterAction.toUpperCase() === 'BR')) {
                    var blockNode = enterAction && enterAction.toUpperCase() === 'DIV' ? createElement('div') : createElement('p');
                    blockNode.appendChild(insertedNode);
                    InsertMethods.AppendBefore(blockNode, parentNode, false);
                }
                else {
                    InsertMethods.AppendBefore(insertedNode, parentNode, false);
                }
                detach(parentNode);
            }
            else {
                InsertMethods.AppendBefore(insertedNode, parentNode.firstChild, false);
            }
        }
        else if (isNOU(preNode.previousSibling) && insertNode.tagName === 'TABLE') {
            parentNode.prepend(insertedNode);
        }
        else {
            parentNode.appendChild(insertedNode);
        }
    };
    // Configures the node selection state after executing the insertion operation.
    InsertHtml.setSelectionAfterInsertion = function (insertedNode, nodeSelection, docElement) {
        if (insertedNode.nodeName === 'IMG') {
            this.mediaFocus(insertedNode, nodeSelection, docElement);
        }
        else if (insertedNode.nodeType !== 3) {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, 0, insertedNode.childNodes.length);
        }
        else {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, 0, insertedNode.textContent.length);
        }
    };
    // Manages insertion operations when nodes are intended to be placed within the current range selection.
    InsertHtml.handleContentInsertionInsideRange = function (docElement, range, nodeSelection, nodeCutter, closestParentNode, insertedNode, isCursor) {
        var liElement = !isNOU(closestParentNode) ?
            closest(closestParentNode, 'li') : null;
        if (this.shouldInsertInTableCell(closestParentNode, liElement, isCursor)) {
            range.extractContents();
            liElement.appendChild(insertedNode);
            this.removeEmptyNextLI(liElement);
        }
        else {
            this.insertWithRangeHandling(docElement, range, nodeCutter, insertedNode, isCursor);
        }
        this.setCursorAfterInsertion(docElement, insertedNode, nodeSelection);
    };
    // Determines if content should be inserted inside a table cell based on the specific conditions.
    InsertHtml.shouldInsertInTableCell = function (closestParentNode, liElement, isCursor) {
        return (!isNOU(closestParentNode) &&
            (closestParentNode.nodeName === 'TD' || closestParentNode.nodeName === 'TH')) &&
            !isNOU(liElement) && !isCursor;
    };
    // Handles direct node insertions by accounting for document structure and browser compatibility factors.
    InsertHtml.insertWithRangeHandling = function (docElement, range, nodeCutter, insertedNode, isCursor) {
        range.deleteContents();
        if (isCursor && range.startContainer.textContent === '' && range.startContainer.nodeName !== 'BR') {
            range.startContainer.innerHTML = '';
        }
        if (Browser.isIE) {
            var frag = docElement.createDocumentFragment();
            frag.appendChild(insertedNode);
            range.insertNode(frag);
        }
        else if (this.isHrElement(range)) {
            this.insertAfterHrElement(range, insertedNode);
        }
        else {
            this.insertBasedOnStartContainer(range, insertedNode, nodeCutter);
        }
    };
    // Handles direct node insertions by accounting for document structure and browser compatibility factors.
    InsertHtml.isHrElement = function (range) {
        return range.startContainer.nodeType === 1 &&
            range.startContainer.nodeName.toLowerCase() === 'hr' &&
            range.endContainer.nodeName.toLowerCase() === 'hr';
    };
    // Handling inserting after horizontal rule elements.
    InsertHtml.insertAfterHrElement = function (range, insertedNode) {
        var paraElem = range.startContainer.nextElementSibling;
        if (paraElem) {
            if (paraElem.querySelector('br')) {
                detach(paraElem.querySelector('br'));
            }
            paraElem.appendChild(insertedNode);
        }
    };
    // Inserts content based on the start container properties and current text structure.
    InsertHtml.insertBasedOnStartContainer = function (range, insertedNode, nodeCutter) {
        var container = range.startContainer;
        if (range.startContainer.nodeName === 'BR') {
            range.startContainer.parentElement.insertBefore(insertedNode, range.startContainer);
        }
        else if (insertedNode.nodeName === 'TABLE' && (CONSTANT.ALLOWED_TABLE_BLOCK_TAGS.indexOf(container.nodeName.toLowerCase()) === -1
            && CONSTANT.TABLE_BLOCK_TAGS.indexOf(container.nodeName.toLowerCase()) === -1)) {
            while ((CONSTANT.ALLOWED_TABLE_BLOCK_TAGS.indexOf(container.parentNode.nodeName.toLowerCase()) === -1
                && CONSTANT.TABLE_BLOCK_TAGS.indexOf(container.parentNode.nodeName.toLowerCase()) === -1)) {
                container = container.parentNode;
            }
            // Case 1: If cursor is at the start of the inline element
            if (range.startOffset === 0 && range.endOffset === 0 && container) {
                container.parentNode.insertBefore(insertedNode, container);
                var hasText = container.textContent.length > 0;
                var hasMediaElements = container.nodeName !== '#text' && container.querySelector('img, video, audio, table') !== null;
                var hasContent = hasText || hasMediaElements;
                if (!hasContent) {
                    detach(container);
                }
            }
            else if (range.startOffset === range.startContainer.textContent.length && range.endOffset ===
                range.startContainer.textContent.length &&
                container && container.parentNode) {
                // Case 2: If cursor is at the end of the inline element
                var nextSibling = container.nextSibling;
                container.parentNode.insertBefore(insertedNode, nextSibling);
            }
            else {
                // Case 3: Handling middle insertion
                var spliceNode = nodeCutter.GetSpliceNode(range, container);
                var nextNode = spliceNode.nextSibling;
                container.parentNode.insertBefore(insertedNode, nextNode);
                detach(spliceNode);
            }
        }
        else {
            range.insertNode(insertedNode);
        }
    };
    // Sets the cursor position after completing the content insertion logic.
    InsertHtml.setCursorAfterInsertion = function (docElement, insertedNode, nodeSelection) {
        if (insertedNode.nodeType !== 3 && insertedNode.childNodes.length > 0) {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, 1, 1);
        }
        else if (insertedNode.nodeName === 'IMG') {
            this.mediaFocus(insertedNode, nodeSelection, docElement);
        }
        else if (insertedNode.nodeType !== 3) {
            nodeSelection.setSelectionContents(docElement, insertedNode);
        }
        else {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, insertedNode.textContent.length, insertedNode.textContent.length);
        }
    };
    // Checks whether the editor should scroll to the cursor position after insertion.
    InsertHtml.shouldScrollToCursor = function (editNode, scrollHeight, insertedNode) {
        return !isNOU(editNode) &&
            scrollHeight < editNode.scrollHeight &&
            insertedNode.nodeType === 1 &&
            (insertedNode.nodeName === 'IMG' || !isNOU(insertedNode.querySelector('img')));
    };
    // Removes empty list items from the associated list after node insertions.
    InsertHtml.removeEmptyNextLI = function (liElement) {
        // Find the root-level list containing this list item
        var rootList = closest(liElement, 'ul,ol');
        // Navigate to the topmost list if this is inside nested lists
        while (rootList && rootList.parentElement && rootList.parentElement.nodeName === 'LI') {
            rootList = closest(rootList.parentElement, 'ul,ol');
        }
        if (!rootList) {
            return;
        }
        // Collect all list items in the list
        var listItems = rootList.querySelectorAll('li');
        // Define a helper to check if a list item is empty (no text and no media elements)
        var isEmptyListItem = function (item) {
            return item.textContent.trim() === '' &&
                !item.querySelector('audio,video,img,table,br');
        };
        // Remove all empty list items
        listItems.forEach(function (item) {
            if (isEmptyListItem(item)) {
                detach(item);
            }
        });
    };
    // Recursively searches for and returns the first text node within the specified node.
    InsertHtml.findFirstTextNode = function (node) {
        if (node.nodeType === Node.TEXT_NODE) {
            return node;
        }
        for (var i = 0; i < node.childNodes.length; i++) {
            var textNode = this.findFirstTextNode(node.childNodes[i]);
            if (!isNOU(textNode)) {
                return textNode;
            }
        }
        return null;
    };
    // Handles HTML content pasting operations & insertHTML execCommand while ensuring context-specific adjustments.
    InsertHtml.pasteInsertHTML = function (nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode, enterAction) {
        var blockElement = this.getImmediateBlockNode(nodes[nodes.length - 1], editNode);
        if (blockElement && blockElement.textContent.length === 0) {
            var brElement = blockElement.querySelector('br:last-of-type');
            if (brElement) {
                brElement.classList.add('rte-temp-br');
            }
        }
        // Initialize key variables and adjust range if needed
        var isCursor = range.startOffset === range.endOffset && range.startContainer === range.endContainer;
        range = this.adjustRangeForEmptyEditor(nodes, range, nodeSelection, docElement, editNode, isCursor);
        // Setup variables for range manipulation
        var rangeInfo = this.setupRangeForPaste(nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode);
        range = rangeInfo.range;
        this.listStyleCleanup(insertedNode);
        // Process based on content structure
        var containsBlockNode = this.containsBlockElements(insertedNode);
        var processBlockElement = (blockElement && blockElement.parentElement && blockElement.parentElement.nodeName === 'LI') ? blockElement.parentElement : blockElement;
        if (!isNOU(processBlockElement) && processBlockElement.nodeName === 'LI') {
            while (processBlockElement.parentElement && (processBlockElement.parentElement.nodeName === 'LI' || processBlockElement.parentElement.nodeName === 'OL' || processBlockElement.parentElement.nodeName === 'UL')) {
                processBlockElement = processBlockElement.parentElement;
            }
            if (processBlockElement && (processBlockElement.nodeName === 'OL' || processBlockElement.nodeName === 'UL')) {
                processBlockElement.classList.add('e-rte-copy-list');
            }
        }
        else {
            insertedNode.childNodes.forEach(function (node) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    var childElement = node;
                    if (childElement.tagName === 'OL' || childElement.tagName === 'UL') {
                        childElement.classList.add('e-rte-copy-list');
                    }
                }
            });
        }
        var lastSelectionNode = containsBlockNode
            ? this.handleBlockNodeContent(nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed)
            : this.handleInlineContent(nodes, insertedNode, range, nodeSelection, docElement, editNode, isCursor, rangeInfo.sibNode, rangeInfo.lasNode, rangeInfo.isSingleNode);
        // Process special cases
        var processedNode = this.processSpecialNodes(lastSelectionNode, insertedNode, enterAction);
        // Position cursor appropriately
        this.positionCursorAfterPaste(processedNode, insertedNode, nodeSelection, docElement, editNode, enterAction);
        // Final cleanup
        this.alignCheck(editNode);
        this.listCleanUp(nodeSelection, docElement);
        this.removeEmptyBrFromParagraph(editNode);
    };
    // Cleans up inline styles applied to list items within the inserted content.
    InsertHtml.listStyleCleanup = function (node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
            var listItems = node.querySelectorAll('li');
            listItems.forEach(function (li) {
                if (li.style.display === 'block') {
                    li.style.removeProperty('display');
                    if (li.getAttribute('style') === '') {
                        li.removeAttribute('style');
                    }
                }
            });
        }
    };
    // Clean up unnecessary line breaks after paste actions.
    InsertHtml.removeEmptyBrFromParagraph = function (editNode) {
        var tempBr = editNode.querySelector('br.rte-temp-br');
        if (tempBr) {
            tempBr.remove();
        }
    };
    // Adjusts range settings when the editor is empty, covering cursor initialization aspects.
    InsertHtml.adjustRangeForEmptyEditor = function (nodes, range, nodeSelection, docElement, editNode, isCursor) {
        if (isCursor && range.startContainer === editNode &&
            editNode.textContent === '' && range.startOffset === 0 && range.endOffset === 0 && editNode.childNodes[0].tagName !== 'TABLE') {
            var currentBlockNode = this.getImmediateBlockNode(nodes[nodes.length - 1], editNode);
            nodeSelection.setSelectionText(docElement, currentBlockNode, currentBlockNode, 0, 0);
            return nodeSelection.getRange(docElement);
        }
        return range;
    };
    // Sets up parameters involving range, sibling nodes, and relevant options for pasting operations.
    InsertHtml.setupRangeForPaste = function (nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode) {
        var preNode;
        var sibNode;
        var lasNode;
        var isSingleNode = false;
        if (editNode !== range.startContainer &&
            ((!isCollapsed && !(closestParentNode.nodeType === Node.ELEMENT_NODE &&
                CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) !== -1))
                || (insertedNode.nodeName.toLowerCase() === 'table' && closestParentNode &&
                    CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) === -1)) && insertedNode.firstChild.nodeName !== 'HR') {
            preNode = nodeCutter.GetSpliceNode(range, closestParentNode);
            if (!isNOU(preNode)) {
                sibNode = isNOU(preNode.previousSibling) ?
                    preNode.parentNode.previousSibling : preNode.previousSibling;
                if (nodes.length === 1) {
                    nodeSelection.setSelectionContents(docElement, preNode);
                    range = nodeSelection.getRange(docElement);
                    isSingleNode = true;
                }
                else {
                    var textContent = nodes[nodes.length - 1].textContent ? nodes[nodes.length - 1].textContent : '';
                    lasNode = nodeCutter.GetSpliceNode(range, nodes[nodes.length - 1].parentElement);
                    if (lasNode && lasNode.nodeName === 'LI' && lasNode.nextSibling && lasNode.nextSibling.nodeName === 'LI') {
                        this.isAnotherLiFromEndLi = textContent === lasNode.textContent ? false : true;
                    }
                    lasNode = isNOU(lasNode) ? preNode : lasNode;
                    nodeSelection.setSelectionText(docElement, preNode, lasNode, 0, (lasNode.nodeType === 3) ? lasNode.textContent.length : lasNode.childNodes.length);
                    range = nodeSelection.getRange(docElement);
                    isSingleNode = false;
                }
            }
        }
        // Clean node content
        this.removingComments(insertedNode);
        return { preNode: preNode, sibNode: sibNode, lasNode: lasNode, isSingleNode: isSingleNode, range: range };
    };
    // Examines whether the inserted node contains block element.
    InsertHtml.containsBlockElements = function (insertedNode) {
        var allChildNodes = insertedNode.childNodes;
        for (var i = 0; i < allChildNodes.length; i++) {
            if (CONSTANT.BLOCK_TAGS.indexOf(allChildNodes[i].nodeName.toLowerCase()) >= 0) {
                return true;
            }
        }
        return false;
    };
    // Processes inline-only content during paste operations for correct insertion.
    InsertHtml.handleInlineContent = function (nodes, insertedNode, range, nodeSelection, docElement, editNode, isCursor, sibNode, lasNode, isSingleNode) {
        var fragment = document.createDocumentFragment();
        if (!isCursor) {
            return this.handleRegularInlineContent(insertedNode, range, fragment, editNode, sibNode, lasNode, isSingleNode);
        }
        else {
            return this.handleCursorInlineContent(nodes, insertedNode, range, nodeSelection, docElement, editNode, fragment);
        }
    };
    // Handles paste operations when dealing with non-collapsed inline selections.
    InsertHtml.handleRegularInlineContent = function (insertedNode, range, fragment, editNode, sibNode, lasNode, isSingleNode) {
        var lastSelectionNode;
        while (insertedNode.firstChild) {
            lastSelectionNode = insertedNode.firstChild;
            fragment.appendChild(insertedNode.firstChild);
        }
        if (isSingleNode) {
            range.deleteContents();
            this.removeEmptyElements(editNode, true);
            range.insertNode(fragment);
        }
        else {
            var startContainerParent = editNode === range.startContainer ?
                range.startContainer : range.startContainer.parentNode;
            var startIndex = Array.prototype.indexOf.call(startContainerParent.childNodes, (Browser.userAgent.indexOf('Firefox') !== -1 && editNode === range.startContainer) ?
                range.startContainer.firstChild : range.startContainer);
            range.deleteContents();
            if (startIndex !== -1) {
                range.setStart(startContainerParent, startIndex);
                range.setEnd(startContainerParent, startIndex);
            }
            if (!isNOU(lasNode) && lasNode !== editNode &&
                editNode.childNodes.length > 0 && editNode.childNodes[0] !== lasNode) {
                detach(lasNode);
                this.removeEmptyElements(editNode, true);
            }
            if (!isNOU(sibNode) && sibNode.parentNode !== editNode.parentNode) {
                if (sibNode.parentNode === editNode) {
                    sibNode.appendChild(fragment);
                }
                else {
                    sibNode.parentNode.appendChild(fragment);
                }
            }
            else {
                range.insertNode(fragment);
            }
        }
        return lastSelectionNode;
    };
    // Handles content insertion when the cursor is placed in an inline context without initial selection.
    InsertHtml.handleCursorInlineContent = function (nodes, insertedNode, range, nodeSelection, docElement, editNode, fragment) {
        var lastSelectionNode;
        var immediateBlockNode = this.getImmediateBlockNode(range.startContainer, editNode);
        var tempSpan = createElement('span', { className: 'tempSpan' });
        if (this.shouldInsertInAnchor(range)) {
            this.insertInAnchor(range, tempSpan, editNode);
        }
        else if (this.isMentionChip(nodes)) {
            range.startContainer.parentElement.insertAdjacentElement('afterend', tempSpan);
        }
        else if (range.startOffset !== 0 && range.endOffset !== 0 && range.startOffset === range.endOffset
            && !insertedNode.querySelector('a') && range.endOffset === range.startContainer.textContent.length && immediateBlockNode.lastChild.contains(range.startContainer)) {
            immediateBlockNode.appendChild(tempSpan);
        }
        else {
            range.insertNode(tempSpan);
        }
        while (insertedNode.firstChild) {
            lastSelectionNode = insertedNode.firstChild;
            fragment.appendChild(insertedNode.firstChild);
        }
        return this.insertFragmentOrReplaceNode(tempSpan, fragment, lastSelectionNode, editNode);
    };
    //Determines if content should be inserted within an anchor element based on specified conditions.
    InsertHtml.shouldInsertInAnchor = function (range) {
        var nearestAnchor = closest(range.startContainer.parentElement, 'a');
        return range.startContainer.nodeType === 3 &&
            !isNOU(nearestAnchor) &&
            !isNOU(closest(nearestAnchor, 'span'));
    };
    // Specifically inserts nodes inside an anchor tag if conditions are met during paste.
    InsertHtml.insertInAnchor = function (range, tempSpan, editNode) {
        var immediateBlockNode = this.getImmediateBlockNode(range.startContainer, editNode);
        if (immediateBlockNode.querySelectorAll('br').length > 0) {
            detach(immediateBlockNode.querySelector('br'));
        }
        var rangeElement = closest(closest(range.startContainer.parentElement, 'a'), 'span');
        rangeElement.appendChild(tempSpan);
    };
    // Checks if the node includes a mentions chip for handling special paste scenarios.
    InsertHtml.isMentionChip = function (nodes) {
        return nodes[0] &&
            nodes[0].nodeName === '#text' &&
            nodes[0].nodeValue.includes('\u200B') &&
            !isNOU(nodes[0].parentElement) &&
            !isNOU(nodes[0].parentElement.previousElementSibling) &&
            nodes[0].parentElement.previousElementSibling.classList.contains('e-mention-chip');
    };
    // Inserts a document fragment at a temporary span position or replaces a specific node.
    InsertHtml.insertFragmentOrReplaceNode = function (tempSpan, fragment, lastSelectionNode, editNode) {
        var matchedElement = this.getClosestMatchingElement(tempSpan.parentNode, fragment, editNode);
        var hasMultipleChildNodes = fragment.firstChild && fragment.firstChild.childNodes.length > 1;
        if (fragment.childNodes.length === 1 && fragment.firstChild && !hasMultipleChildNodes && matchedElement) {
            return this.replaceWithMatchedContent(tempSpan, fragment, lastSelectionNode);
        }
        else {
            tempSpan.parentNode.replaceChild(fragment, tempSpan);
            return lastSelectionNode;
        }
    };
    // Replaces the temporary node with matched content, adjusting text nodes if required.
    InsertHtml.replaceWithMatchedContent = function (tempSpan, fragment, lastSelectionNode) {
        var wrapperDiv = document.createElement('div');
        wrapperDiv.innerHTML = fragment.firstChild.innerHTML || '';
        var result = lastSelectionNode;
        if (!isNOU(wrapperDiv.firstChild)) {
            this.addCursorMarker(wrapperDiv, true);
            tempSpan.outerHTML = wrapperDiv.innerHTML;
        }
        wrapperDiv.remove();
        return result;
    };
    // Manages block node insertion during paste operations to align with document structure.
    InsertHtml.handleBlockNodeContent = function (nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed) {
        var parentElem = this.findParentPreElement(range, editNode);
        if (!isNOU(insertedNode) && !isNOU(parentElem) && parentElem.nodeName === 'PRE') {
            range.insertNode(insertedNode);
            return insertedNode.lastChild;
        }
        else {
            return this.processBlockContent(nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed);
        }
    };
    // Finds the nearest parent PRE element starting from the current range container.
    InsertHtml.findParentPreElement = function (range, editNode) {
        var parentElem = range.startContainer;
        while (!isNOU(parentElem) && parentElem.nodeName !== 'PRE' && parentElem !== editNode) {
            parentElem = parentElem.parentElement;
        }
        return parentElem;
    };
    /* Processes the inserted nodes, preserving initial nodes until first block element,
    then wrapping inline nodes between blocks with appropriate container elements */
    InsertHtml.processInlineNodesBetweenBlocks = function (insertedNode, enterAction) {
        var fragment = document.createDocumentFragment();
        var foundFirstBlock = false;
        var currentGroup = null;
        var lastNode = null;
        var tempElement = createElement('div', { id: 'pasteContent_rte' });
        while (insertedNode.firstChild) {
            var currentNode = insertedNode.firstChild;
            // Skip empty text nodes
            if (currentNode.nodeName === '#text' && currentNode.textContent.trim() === '') {
                detach(currentNode);
                continue;
            }
            // Keep track of last processed node
            lastNode = currentNode;
            // Check if this is a block element
            var isBlockNode = this.isBlockElement(currentNode);
            if (!foundFirstBlock) {
                // Before first block is encountered, preserve original structure
                if (isBlockNode) {
                    // First block found, change mode
                    foundFirstBlock = true;
                    fragment.appendChild(currentNode);
                }
                else {
                    tempElement.appendChild(currentNode);
                    fragment.appendChild(tempElement);
                }
            }
            else {
                // After first block, apply wrapping logic
                if (isBlockNode) {
                    // Add block elements directly, close any open group
                    currentGroup = null;
                    fragment.appendChild(currentNode);
                }
                else {
                    // Wrap inline/text nodes
                    if (!currentGroup) {
                        // Create new wrapper if needed
                        currentGroup = enterAction === 'DIV' ?
                            createElement('div') : createElement('p');
                        fragment.appendChild(currentGroup);
                    }
                    // Add to current group
                    currentGroup.appendChild(currentNode);
                }
            }
        }
        return { fragment: fragment, lastNode: lastNode };
    };
    // Checks whether the given node is a block element.
    InsertHtml.isBlockElement = function (node) {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return false;
        }
        var blockTags = CONSTANT.BLOCK_TAGS;
        var nodeName = node.nodeName.toLowerCase();
        for (var i = 0; i < blockTags.length; i++) {
            if (blockTags[i] === nodeName) {
                return true;
            }
        }
        return false;
    };
    // Processes block elements during insertion, wrapping and positioning elements as needed.
    InsertHtml.processBlockContent = function (nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed) {
        var lastSelectionNode = null;
        var insertedFragment = this.processInlineNodesBetweenBlocks(insertedNode, enterAction);
        // Insert a temporary node and get ready to process content
        lastSelectionNode = this.insertTempNode(range, insertedFragment.fragment, nodes, nodeCutter, editNode);
        // Delete existing contents if needed
        if (!this.contentsDeleted) {
            this.cleanupBeforeBlockInsertion(range, editNode, isCollapsed);
        }
        var inlineNodeWrapper = editNode.querySelector('#pasteContent_rte');
        if (!isNOU(inlineNodeWrapper)) {
            this.processFirstInlineNodeSet(inlineNodeWrapper, enterAction);
        }
        return lastSelectionNode;
    };
    // Performs necessary cleanup actions prior to block element insertion, like removing empties.
    InsertHtml.cleanupBeforeBlockInsertion = function (range, editNode, isCollapsed) {
        if (!isCollapsed &&
            range.startContainer.parentElement.textContent.length === 0 &&
            range.startContainer.nodeName === 'BR' &&
            range.startContainer.parentElement.nodeName === 'P') {
            editNode.removeChild(range.startContainer.parentElement);
        }
        range.deleteContents();
        this.removeEmptyElements(editNode);
    };
    // Processes and adjusts the first set of inline nodes before any block.
    InsertHtml.processFirstInlineNodeSet = function (insertedNode, enterAction) {
        var lastSelectionNode;
        while (insertedNode.firstChild) {
            lastSelectionNode = insertedNode.firstChild;
            if (this.isInlineElement(lastSelectionNode)) {
                lastSelectionNode = this.handleFirstBlockChild(insertedNode, enterAction);
            }
            else {
                break; // Prevent infinite loop
            }
        }
        detach(insertedNode);
        return lastSelectionNode;
    };
    // Moves the first set of inline nodes to the previous block element a block.
    InsertHtml.handleFirstBlockChild = function (insertedNode, enterAction) {
        var firstChild = insertedNode.firstChild;
        // Ensure there's a previous element sibling
        if (isNOU(insertedNode.previousElementSibling)) {
            var firstParaElm = enterAction === 'DIV' ? createElement('div') : createElement('p');
            insertedNode.parentElement.insertBefore(firstParaElm, insertedNode);
        }
        // Insert based on previous sibling type
        if (insertedNode.previousElementSibling.nodeName === 'BR') {
            insertedNode.parentElement.insertBefore(insertedNode.firstChild, insertedNode);
        }
        else {
            insertedNode.previousElementSibling.appendChild(insertedNode.firstChild);
        }
        return firstChild;
    };
    // Checks if a given node is an inline node.
    InsertHtml.isInlineElement = function (node) {
        return node.nodeName === '#text' ||
            (this.inlineNode.indexOf(node.nodeName.toLowerCase()) >= 0);
    };
    // Handles special cases in node structures that require custom processing post-insertion.
    InsertHtml.processSpecialNodes = function (lastSelectionNode, insertedNode, enterAction) {
        if (!lastSelectionNode) {
            return null;
        }
        // Handle Google Sheets HTML
        if (lastSelectionNode instanceof Element && lastSelectionNode.nodeName === 'GOOGLE-SHEETS-HTML-ORIGIN') {
            return this.processGoogleSheetsTable(lastSelectionNode);
        }
        // Handle table nodes to insert paragraphs after tables if there is no content after table.
        if (lastSelectionNode.nodeName === 'TABLE') {
            return this.addParagraphAfterTable(lastSelectionNode, enterAction);
        }
        return lastSelectionNode;
    };
    // Processes table nodes that originate from Google Sheets for alignment adjustments.
    InsertHtml.processGoogleSheetsTable = function (node) {
        var tableEle = node.querySelector('table');
        var colGroup = tableEle.querySelector('colgroup');
        if (colGroup) {
            for (var i = 0; i < tableEle.rows.length; i++) {
                for (var k = 0; k < tableEle.rows[i].cells.length; k++) {
                    var col = colGroup.querySelectorAll('col')[k];
                    if (col && col.hasAttribute('width')) {
                        var width = col.getAttribute('width');
                        tableEle.rows[i].cells[k].style.width = width + 'px';
                    }
                }
            }
        }
        return node;
    };
    // Inserts a paragraph after a table node to ensure continuity in the document.
    InsertHtml.addParagraphAfterTable = function (tableNode, enterAction) {
        var pTag = createElement(enterAction === 'DIV' ? 'div' : 'p');
        pTag.appendChild(createElement('br'));
        tableNode.parentElement.insertBefore(pTag, tableNode.nextSibling);
        return pTag;
    };
    // Positions the editor cursor appropriately after completing a paste operation.
    InsertHtml.positionCursorAfterPaste = function (lastSelectionNode, insertedNode, nodeSelection, docElement, editNode, enterAction) {
        if (!lastSelectionNode) {
            return;
        }
        if (lastSelectionNode.nodeName === '#text') {
            this.placeCursorEnd(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode);
        }
        else if (lastSelectionNode.nodeName === 'HR') {
            this.handleHRElementCursor(lastSelectionNode, nodeSelection, docElement, enterAction);
        }
        else if (editNode.contains(lastSelectionNode) && isNOU(editNode.querySelector('.paste-cursor'))) {
            this.cursorPos(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode);
        }
        else {
            this.handleListElementCursor(insertedNode, editNode, nodeSelection, docElement);
        }
    };
    InsertHtml.handleListElementCursor = function (insertedNode, editNode, nodeSelection, docElement) {
        var cursorElm = editNode.querySelector('.paste-cursor');
        var nodeList = editNode.querySelectorAll('.pasteContent_RTE');
        var brElement = editNode.querySelector('br.rte-temp-br');
        if (!isNOU(cursorElm)) {
            nodeSelection.setCursorPoint(docElement, cursorElm, 0);
            cursorElm.remove();
        }
        else if (nodeList.length > 0) {
            var lastElement = nodeList[nodeList.length - 1];
            this.cursorPos(lastElement, insertedNode, nodeSelection, docElement, editNode);
        }
        else if (!isNOU(brElement)) {
            nodeSelection.setCursorPoint(docElement, brElement, 0);
        }
    };
    // Handles cursor placement after inserting horizontal rule elements in the document.
    InsertHtml.handleHRElementCursor = function (lastSelectionNode, nodeSelection, docElement, enterAction) {
        var nextSiblingNode = lastSelectionNode.nextSibling;
        while (nextSiblingNode && nextSiblingNode.nodeName === '#text' && nextSiblingNode.textContent.trim() === '') {
            nextSiblingNode = nextSiblingNode.nextSibling;
        }
        var siblingTag = createElement(enterAction === 'DIV' ? 'div' : 'p');
        siblingTag.appendChild(createElement('br'));
        var parentNode = lastSelectionNode.parentNode;
        if (nextSiblingNode && (nextSiblingNode.nodeName === 'HR' || nextSiblingNode.nodeName === 'TABLE')) {
            parentNode.insertBefore(siblingTag, nextSiblingNode);
            lastSelectionNode = siblingTag;
        }
        else if (parentNode && parentNode.nodeName === 'LI') {
            var currentNode = lastSelectionNode.nextSibling;
            // Traverse through siblings of the <hr> to find a valid non-empty node
            while (currentNode && (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent.trim() === '')) {
                currentNode = currentNode.nextSibling;
            }
            // If no valid sibling is found, move up to the parent and check for the parent's siblings
            while (!currentNode && parentNode) {
                if (parentNode && (parentNode.nodeName === 'OL' || parentNode.nodeName === 'UL' || parentNode.nodeName === 'LI' || parentNode.nodeName === 'BLOCKQUOTE')) {
                    currentNode = parentNode.nextSibling;
                    // Traverse parent's siblings
                    while (currentNode && (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent.trim() === '')) {
                        currentNode = currentNode.nextSibling;
                    }
                }
                parentNode = parentNode.parentNode;
            }
            if (isNOU(currentNode)) {
                lastSelectionNode.parentNode.appendChild(siblingTag);
            }
            lastSelectionNode = currentNode ? currentNode : siblingTag;
        }
        else if (nextSiblingNode) {
            var firstChildElement = nextSiblingNode.firstChild;
            if (firstChildElement && firstChildElement.nodeName !== '#text' && firstChildElement.hasAttribute('class') && firstChildElement.classList.contains('rte-temp-br')) {
                nextSiblingNode.firstChild.removeAttribute('class');
            }
            lastSelectionNode = nextSiblingNode;
        }
        else {
            parentNode.appendChild(siblingTag);
            parentNode.insertBefore(lastSelectionNode, siblingTag);
            lastSelectionNode = siblingTag;
        }
        nodeSelection.setSelectionText(docElement, lastSelectionNode, lastSelectionNode, 0, 0);
        return lastSelectionNode;
    };
    // Compares two elements to ensure they are equivalent in terms of tag and relevant attributes.
    InsertHtml.compareParentElements = function (el1, el2) {
        if (!el1 || !el2) {
            return false;
        }
        if (el1.tagName !== el2.tagName) {
            return false;
        }
        return this.getFilteredAttributes(el1) === this.getFilteredAttributes(el2);
    };
    // Retrieves attributes of an element, filtering out the non-relevant ones for comparison.
    InsertHtml.getFilteredAttributes = function (element) {
        return Array.from(element.attributes)
            .map(function (attr) {
            if (attr.name === 'class') {
                var filteredClass = attr.value.split(' ')
                    .filter(function (cls) { return cls !== 'pasteContent_RTE'; })
                    .join(' ');
                return filteredClass ? "class='" + filteredClass + "'" : '';
            }
            return attr.name + "='" + attr.value + "'";
        })
            .filter(function (attr) { return attr.length > 0; })
            .sort()
            .join(' ');
    };
    // Identifies the closest matching element in the document fragment from the current node.
    InsertHtml.getClosestMatchingElement = function (startNode, fragment, editNode) {
        var currentNode = startNode;
        while (currentNode && !currentNode.contains(editNode)) {
            var matchingPastedNode = this.findMatchingChild(fragment, currentNode);
            if (matchingPastedNode) {
                return currentNode;
            }
            currentNode = currentNode.parentElement;
        }
        return null;
    };
    // Finds a child within a parent container that matches the target node by structural properties.
    InsertHtml.findMatchingChild = function (fragment, targetNode) {
        for (var _i = 0, _a = Array.from(fragment.children); _i < _a.length; _i++) {
            var node = _a[_i];
            if (this.compareParentElements(node, targetNode)) {
                return node;
            }
            var deeperMatch = this.findMatchingChild(node, targetNode);
            if (deeperMatch) {
                return deeperMatch;
            }
        }
        return null;
    };
    // Executes cleanup operations on lists to ensure consistency after paste operation.
    InsertHtml.listCleanUp = function (nodeSelection, docElement) {
        var range = nodeSelection.getRange(docElement);
        var startContainer = range.startContainer;
        var startOffset = range.startOffset;
        var hasListCleanUp = false;
        var hasListContainerCleanUp = false;
        var copiedLists = docElement.querySelectorAll('.e-rte-copy-list');
        for (var i = 0; i < copiedLists.length; i++) {
            var listItem = copiedLists[i];
            hasListCleanUp = this.cleanUpListItems(listItem);
            hasListContainerCleanUp = this.cleanUpListContainer(listItem);
            listItem.classList.remove('e-rte-copy-list');
            if (listItem.getAttribute('class').length === 0) {
                listItem.removeAttribute('class');
            }
        }
        if (hasListCleanUp || hasListContainerCleanUp) {
            range.setStart(startContainer, startOffset);
            range.setEnd(startContainer, startOffset);
        }
    };
    // Cleans up list items to restore structural integrity and resolve any post-paste issues.
    InsertHtml.cleanUpListItems = function (parentContainer) {
        var _this = this;
        var hasListCleanUp = false;
        var listItems;
        if (!isNOU(parentContainer.closest('ol, ul'))) {
            listItems = parentContainer.closest('ol, ul').querySelectorAll('li');
        }
        if (isNOU(listItems) || listItems.length === 0) {
            return false;
        }
        var nearestListItem = null;
        listItems.forEach(function (listItem) {
            var parentElement = listItem.parentElement;
            if (!isNOU(parentElement) && parentElement.nodeName !== 'OL' && parentElement.nodeName !== 'UL') {
                if (isNOU(nearestListItem)) {
                    nearestListItem = parentElement.closest('li');
                }
                if (!isNOU(nearestListItem)) {
                    var nextSibling = listItem.nextSibling;
                    if (!isNOU(nextSibling) && nextSibling.nodeName !== 'LI') {
                        var startIndex = Array.prototype.indexOf.call(parentElement.childNodes, nextSibling);
                        var clonedParent = parentElement.cloneNode(false);
                        var totalChildren = parentElement.childNodes.length;
                        for (var i = startIndex; i < totalChildren; i++) {
                            clonedParent.appendChild(parentElement.childNodes[startIndex]);
                        }
                        if (clonedParent.childNodes.length > 0) {
                            var newListItem = document.createElement('li');
                            newListItem.appendChild(clonedParent);
                            nearestListItem.insertAdjacentElement('afterend', newListItem);
                        }
                        else {
                            clonedParent.remove();
                        }
                    }
                    var closestList = parentElement.closest('ol, ul');
                    nearestListItem.insertAdjacentElement('afterend', listItem);
                    nearestListItem = nearestListItem.nextSibling;
                    if (!isNOU(closestList)) {
                        _this.removeEmptyElements(closestList);
                    }
                    hasListCleanUp = true;
                }
            }
        });
        var cleanUpFlattenListContainer = this.cleanUpFlattenListContainer(parentContainer);
        hasListCleanUp = cleanUpFlattenListContainer ? cleanUpFlattenListContainer : hasListCleanUp;
        return hasListCleanUp;
    };
    // Manages cleanup processes for deeply nested list elements as necessary.
    InsertHtml.cleanUpFlattenListContainer = function (parentContainer) {
        var hasListCleanUp = false;
        var listItems;
        if (!isNOU(parentContainer.closest('ol, ul'))) {
            listItems = parentContainer.closest('ol, ul').querySelectorAll('li');
        }
        if (isNOU(listItems) || listItems.length === 0) {
            return false;
        }
        listItems.forEach(function (listItem) {
            if (!isNOU(listItem.firstChild) && (listItem.firstChild.nodeName === 'OL' || listItem.firstChild.nodeName === 'UL')) {
                listItem.style.listStyleType = 'none';
            }
            var nestedLi = Array.from(listItem.children).find(function (child) {
                return child.tagName === 'LI' && (child.parentElement && child.parentElement.tagName !== 'OL' && child.parentElement.tagName !== 'UL');
            });
            if (!isNOU(nestedLi) && !isNOU(listItem.parentNode)) {
                listItem.parentNode.replaceChild(nestedLi, listItem);
                if (isNOU(nestedLi.textContent) || nestedLi.textContent.trim() === '') {
                    nestedLi.remove();
                }
                hasListCleanUp = true;
            }
        });
        return hasListCleanUp;
    };
    // Resolves inconsistencies within list containers, ensuring no stray elements are left.
    InsertHtml.cleanUpListContainer = function (parentList) {
        var hasListContainerCleanUp = false;
        var nonLiElementCollection = [];
        var replacements = [];
        if (!isNOU(parentList)) {
            for (var i = 0; i < parentList.childNodes.length; i++) {
                var childNode = parentList.childNodes[i];
                var isListNode = ['UL', 'OL'].indexOf(childNode.nodeName) !== -1;
                var hasEmptyTextSibling = isNOU(childNode.previousSibling) || (childNode.previousSibling &&
                    childNode.previousSibling.nodeType === Node.TEXT_NODE &&
                    childNode.previousSibling.textContent.trim() === '');
                var prevElement = childNode.previousElementSibling;
                var isPrevLi = prevElement && prevElement.nodeName.toUpperCase() === 'LI';
                if (isListNode && (hasEmptyTextSibling || isPrevLi) && prevElement) {
                    prevElement.appendChild(childNode);
                    this.cleanUpListContainer(childNode);
                    i--;
                    hasListContainerCleanUp = true;
                }
                else if (childNode.nodeName.toLocaleUpperCase() !== 'LI') {
                    nonLiElementCollection.push(childNode);
                }
                if ((childNode.nodeName.toLocaleUpperCase() === 'LI' || parentList.lastChild === childNode) && nonLiElementCollection.length > 0) {
                    replacements.push({ elements: nonLiElementCollection.slice() });
                    nonLiElementCollection = [];
                }
            }
            replacements.forEach(function (_a) {
                var elements = _a.elements;
                var newListItem = document.createElement('li');
                elements[0].parentNode.replaceChild(newListItem, elements[0]);
                elements.forEach(function (child) { return newListItem.appendChild(child); });
                if (newListItem.textContent && newListItem.textContent.trim() === '' && !newListItem.querySelector('img')) {
                    parentList.removeChild(newListItem);
                }
                if (newListItem.firstElementChild && (newListItem.firstElementChild.nodeName === 'OL' || newListItem.firstElementChild.nodeName === 'UL')) {
                    newListItem.style.listStyleType = 'none';
                }
                hasListContainerCleanUp = true;
            });
        }
        return hasListContainerCleanUp;
    };
    // Moves the cursor to the end of the content node, ensuring proper placement.
    InsertHtml.placeCursorEnd = function (lastSelectionNode, insertedNode, nodeSelection, docElement, editNode) {
        while (!isNOU(lastSelectionNode) && lastSelectionNode.nodeName !== '#text' && lastSelectionNode.nodeName !== 'IMG' &&
            lastSelectionNode.nodeName !== 'VIDEO' && lastSelectionNode.nodeName !== 'AUDIO' && lastSelectionNode.nodeName !== 'BR' && lastSelectionNode.nodeName !== 'HR') {
            if (!isNOU(lastSelectionNode.lastChild) && (lastSelectionNode.lastChild.nodeName === 'P' &&
                lastSelectionNode.lastChild.innerHTML === '')) {
                var lineBreak = createElement('br');
                lastSelectionNode.lastChild.appendChild(lineBreak);
            }
            lastSelectionNode = lastSelectionNode.lastChild;
        }
        lastSelectionNode = isNOU(lastSelectionNode) ? insertedNode : lastSelectionNode;
        if (lastSelectionNode.nodeName === 'IMG' || lastSelectionNode.nodeName === 'VIDEO' || lastSelectionNode.nodeName === 'AUDIO') {
            this.mediaFocus(lastSelectionNode, nodeSelection, docElement);
        }
        else {
            nodeSelection.setSelectionText(docElement, lastSelectionNode, lastSelectionNode, lastSelectionNode.textContent.length, lastSelectionNode.textContent.length);
        }
        this.removeEmptyElements(editNode);
    };
    // Retrieves a collection of nodes from the current selection range for insertion purposes.
    InsertHtml.getNodeCollection = function (range, nodeSelection, insertedNode) {
        var nodes = [];
        if (range.startOffset === range.endOffset && range.startContainer === range.endContainer &&
            range.startContainer.nodeName !== 'BR' && range.startContainer.childNodes.length > 0 &&
            (range.startContainer.nodeName === 'TD' || (range.startContainer.nodeType !== 3 &&
                insertedNode.classList && insertedNode.classList.contains('pasteContent')))) {
            nodes.push(range.startContainer.childNodes[range.endOffset]);
        }
        else {
            nodes = nodeSelection.getInsertNodeCollection(range);
        }
        return nodes;
    };
    // Inserts a temporary node at the appropriate position based on range state and node types.
    InsertHtml.insertTempNode = function (range, insertedNode, nodes, nodeCutter, editNode) {
        var lastSelectionNode = insertedNode.lastChild;
        // Handle insertion after a TABLE when selection is at editor root
        if (this.shouldInsertAfterTable(range, editNode)) {
            this.insertNodeAfterTable(range.startContainer, insertedNode, range.endOffset - 1);
            return lastSelectionNode;
        }
        // Handle insertion before a TABLE when selection is at editor root
        if (this.shouldInsertBeforeTable(range, editNode)) {
            this.insertNodeBeforeTable(range.startContainer, insertedNode, range.startOffset);
            return lastSelectionNode;
        }
        // Handle insertion at the end of editor when table is at cursor
        if (this.shouldAppendAfterTableAtCursor(range, editNode)) {
            range.startContainer.appendChild(insertedNode);
            return lastSelectionNode;
        }
        // Standard insertion cases
        lastSelectionNode = this.handleStandardNodeInsertion(range, insertedNode, nodes, nodeCutter, editNode);
        return lastSelectionNode;
    };
    // Checks if we should insert after a table element at editor root.
    InsertHtml.shouldInsertAfterTable = function (range, editNode) {
        var startContainer = range.startContainer.nodeType === Node.TEXT_NODE
            ? range.startContainer.parentNode
            : range.startContainer;
        return (startContainer === editNode || startContainer.closest('table')) &&
            !isNOU(startContainer.childNodes[range.endOffset - 1]) &&
            startContainer.childNodes[range.endOffset - 1].nodeName === 'TABLE';
    };
    // Inserts node after a table element.
    InsertHtml.insertNodeAfterTable = function (container, insertedNode, index) {
        if (isNOU(container.childNodes[index].nextSibling)) {
            container.appendChild(insertedNode);
        }
        else {
            container.insertBefore(insertedNode, container.childNodes[index].nextSibling);
        }
    };
    InsertHtml.shouldInsertBeforeTable = function (range, editNode) {
        return range.startContainer === editNode &&
            !isNOU(range.startContainer.childNodes[range.startOffset]) &&
            range.startContainer.childNodes[range.startOffset].nodeName === 'TABLE';
    };
    InsertHtml.insertNodeBeforeTable = function (container, insertedNode, index) {
        if (index >= 0 && index < container.childNodes.length) {
            container.insertBefore(insertedNode, container.childNodes[index]);
        }
        else {
            container.appendChild(insertedNode);
        }
    };
    // Checks if we should append after a table at cursor position
    InsertHtml.shouldAppendAfterTableAtCursor = function (range, editNode) {
        return range.startContainer === editNode &&
            !isNOU(range.startContainer.childNodes[range.endOffset]) &&
            range.startContainer.childNodes[range.endOffset].nodeName === 'TABLE';
    };
    // Handles standard node insertion cases.
    InsertHtml.handleStandardNodeInsertion = function (range, insertedNode, nodes, nodeCutter, editNode) {
        // Find appropriate block node for insertion
        var blockNode = this.findBlockNodeForInsertion(range, nodes, editNode);
        // Handle list-specific processing for inserted nodes
        this.processListItemsInNode(blockNode, insertedNode, editNode);
        var lastSelectionNode = insertedNode.lastChild;
        // Handle table cell insertion
        if (this.isTableCellNode(blockNode)) {
            this.insertInTableCell(range, insertedNode, blockNode, nodeCutter);
            return lastSelectionNode;
        }
        var emptyBlockEle = this.isHorizontalRuleInEmptyBlock(lastSelectionNode, range);
        // When inserting HR and selection is in a P/DIV with only BR
        if (!isNOU(emptyBlockEle)) {
            var containerParent = emptyBlockEle.parentNode;
            containerParent.replaceChild(insertedNode, emptyBlockEle);
            return lastSelectionNode;
        }
        // Handle media elements
        if (this.isMediaElement(blockNode)) {
            blockNode = range.startContainer;
        }
        // Handle other insertion cases
        this.handleRegularInsertion(range, insertedNode, blockNode, nodeCutter, editNode);
        return lastSelectionNode;
    };
    // Finds appropriate block node for insertion.
    InsertHtml.findBlockNodeForInsertion = function (range, nodes, editNode) {
        var blockNode = this.getImmediateBlockNode(nodes[nodes.length - 1], editNode);
        // Fallback to range end container if no block node found
        if ((isNOU(blockNode) || isNOU(blockNode.parentElement)) && range.endContainer.nodeType !== 3) {
            blockNode = range.endContainer;
            range.setEnd(blockNode, range.endContainer.textContent.length);
        }
        // Special handling for body/div block nodes
        if (blockNode && (blockNode.nodeName === 'BODY' ||
            (blockNode.nodeName === 'DIV' && range.startContainer === range.endContainer && range.startContainer.nodeType === 1))) {
            blockNode = range.startContainer;
        }
        return blockNode;
    };
    // Processes list items in a node being inserted inside a list context.
    InsertHtml.processListItemsInNode = function (blockNode, insertedNode, editNode) {
        this.wrapUnstructedLiWithUl(insertedNode);
        // Only process if we're in a list item and inserting a list
        if (!this.shouldProcessListItems(blockNode, insertedNode, editNode)) {
            return;
        }
        var liNode;
        var insertedNodeAsHtml = insertedNode;
        // Extract LI elements from the list and normalize their styles
        while (!isNOU(insertedNodeAsHtml.firstElementChild) &&
            insertedNodeAsHtml.firstElementChild.lastElementChild &&
            insertedNodeAsHtml.firstElementChild.lastElementChild.tagName === 'LI') {
            liNode = insertedNodeAsHtml.firstElementChild.lastElementChild;
            this.removeChecklistStyle(blockNode, liNode);
            this.removeListItemMargins(liNode);
            insertedNodeAsHtml.firstElementChild.insertAdjacentElement('afterend', liNode);
        }
    };
    /*
    * Wraps loose `<li>` elements within a `<ul>` container to ensure proper list structure.
    */
    InsertHtml.wrapUnstructedLiWithUl = function (insertedNode) {
        var orphanLIs = Array.from(insertedNode.querySelectorAll('li')).filter(function (li) {
            return ['UL', 'OL'].indexOf(li.parentNode.nodeName) === -1;
        });
        var ul = null;
        var siblingFlag;
        for (var i = 0; i < orphanLIs.length; i++) {
            var currentLi = orphanLIs[i];
            var isSibling = orphanLIs[i + 1] && currentLi.nextSibling === orphanLIs[i + 1];
            if (isNOU(ul) || !siblingFlag) {
                ul = createElement('ul');
                insertedNode.insertBefore(ul, currentLi);
            }
            ul.appendChild(currentLi);
            siblingFlag = isSibling;
        }
    };
    /*
    * Removes checklist-specific inline styles from a pasted list item (`<li>`).
    */
    InsertHtml.removeChecklistStyle = function (blockNode, liNode) {
        if (blockNode.nodeName === 'LI' && blockNode.parentElement &&
            (blockNode.parentElement.nodeName === 'UL' || blockNode.parentElement.nodeName === 'OL')) {
            liNode.style.removeProperty('list-style');
            liNode.style.removeProperty('position');
            if (liNode.getAttribute('style') === '') {
                liNode.removeAttribute('style');
            }
        }
    };
    // Checks if we should process list items in the node.
    InsertHtml.shouldProcessListItems = function (blockNode, insertedNode, editNode) {
        return blockNode &&
            blockNode.nodeName !== '#text' &&
            blockNode.closest('LI') &&
            editNode.contains(blockNode.closest('LI')) &&
            blockNode.nodeName !== 'TD' &&
            blockNode.nodeName !== 'TH' &&
            blockNode.nodeName !== 'TR' &&
            insertedNode &&
            insertedNode.firstElementChild &&
            (insertedNode.firstElementChild.tagName === 'OL' ||
                insertedNode.firstElementChild.tagName === 'UL');
    };
    // Removes margin properties from a list item
    InsertHtml.removeListItemMargins = function (liNode) {
        liNode.style.removeProperty('margin-left');
        liNode.style.removeProperty('margin-top');
        liNode.style.removeProperty('margin-bottom');
        if (liNode.getAttribute('style') === '') {
            liNode.removeAttribute('style');
        }
    };
    // Checks if the node is a table cell
    InsertHtml.isTableCellNode = function (blockNode) {
        if (!blockNode) {
            return false;
        }
        var nodeName = blockNode.nodeName;
        return nodeName === 'TD' || nodeName === 'TH' || nodeName === 'TR' || nodeName === 'TABLE';
    };
    // Handles insertion in a table cell.
    InsertHtml.insertInTableCell = function (range, insertedNode, blockNode, nodeCutter) {
        var parentElem = range.startContainer;
        // Check if parentElem is TD or TH and contains only a BR element
        if ((parentElem.nodeName === 'TD' || parentElem.nodeName === 'TH') && parentElem.childNodes.length === 1 && parentElem.firstChild.nodeName === 'BR') {
            // Replace BR with HR
            parentElem.replaceChild(insertedNode, parentElem.firstChild);
            this.contentsDeleted = true;
            return; // Exit the function after directly replacing
        }
        // Find direct child of the table cell
        while (!isNOU(parentElem) && parentElem.parentElement !== blockNode) {
            parentElem = parentElem.parentElement;
        }
        range.deleteContents();
        var splitedElm = nodeCutter.GetSpliceNode(range, parentElem);
        if (splitedElm) {
            splitedElm.parentNode.replaceChild(insertedNode, splitedElm);
        }
        else {
            range.insertNode(insertedNode);
        }
        this.contentsDeleted = true;
    };
    // Handles regular insertion cases.
    InsertHtml.handleRegularInsertion = function (range, insertedNode, blockNode, nodeCutter, editNode) {
        var nodeSelection = new NodeSelection(editNode);
        var currentNodes = this.getNodeCollection(range, nodeSelection, insertedNode);
        var currentNode = currentNodes[currentNodes.length - 1];
        var splitedElm;
        // Check if the node is an empty special node (BR, HR, or empty text in LI).
        if (this.isEmptySpecialNode(currentNode)) {
            splitedElm = currentNode;
            if (this.handleEmptySpecialNodeInsertion(range, insertedNode, currentNode)) {
                return; // Only return if fully handled.
            }
        }
        // Check if the node is text or BR in a list item with content.
        else if (this.isTextOrBrInListItem(currentNode, blockNode, editNode)) {
            splitedElm = currentNode;
            if (this.handleTextInListItem(range, insertedNode, currentNode, blockNode, nodeCutter, editNode)) {
                return; // Only return if fully handled.
            }
        }
        // Handle regular node insertion.
        else {
            splitedElm = this.getSplitElementForInsertion(range, nodeCutter, blockNode);
        }
        if (!isNOU(splitedElm) && splitedElm === editNode) {
            range.deleteContents();
            this.removeEmptyElements(editNode);
            range.insertNode(insertedNode);
            this.contentsDeleted = true;
        }
        else if (splitedElm && splitedElm.nodeType === Node.ELEMENT_NODE && range.toString() === '' &&
            splitedElm.querySelector('img, video, audio') !== null) {
            splitedElm.parentNode.insertBefore(insertedNode, splitedElm);
        }
        else {
            // Common replacement logic for all paths that don't return early.
            splitedElm.parentNode.replaceChild(insertedNode, splitedElm);
        }
    };
    // Checks if the node is an empty special node (BR, HR or empty text in LI).
    InsertHtml.isEmptySpecialNode = function (currentNode) {
        return !!currentNode &&
            ((currentNode.nodeName === 'BR' || currentNode.nodeName === 'HR' ||
                (currentNode.nodeName === '#text' &&
                    !isNOU(currentNode.parentElement) &&
                    currentNode.parentElement.nodeName === 'LI')) &&
                (!isNOU(currentNode.parentElement) &&
                    currentNode.parentElement.textContent.trim().length === 0));
    };
    // Handles insertion when the current node is an empty special node.
    InsertHtml.handleEmptySpecialNodeInsertion = function (range, insertedNode, currentNode) {
        if (currentNode.parentElement.nodeName === 'LI' &&
            !isNOU(currentNode.nextSibling) &&
            currentNode.nextSibling.nodeName === 'BR') {
            detach(currentNode.nextSibling);
        }
        if ((currentNode.parentElement.nodeName === 'LI' || currentNode.parentElement.closest('li')) &&
            currentNode.parentElement.textContent === '') {
            this.removeListfromPaste(range);
            if (currentNode.parentElement.childNodes.length === 1 &&
                currentNode.nodeName === 'BR') {
                detach(currentNode);
            }
            var filteredChildNodes = Array.from(insertedNode.childNodes).filter(function (child) {
                return !(child.nodeName === 'LI' || child.nodeName === 'UL' || child.nodeName === 'OL');
            });
            var insertNodes = this.extractChildNodes(insertedNode);
            if (filteredChildNodes.length > 0 && insertNodes.length > 1) {
                this.insertBlockNodesInLI(insertNodes, range);
            }
            else {
                var startContainerParent = range.startContainer.parentElement;
                var nextSibling = startContainerParent.nextElementSibling;
                if (range.startContainer.nodeName === 'DIV' && startContainerParent &&
                    startContainerParent.nodeName === 'LI' && startContainerParent.parentNode) {
                    startContainerParent.parentNode.insertBefore(insertedNode, nextSibling);
                    detach(startContainerParent); // Detach the parent element after inserting
                }
                else {
                    range.insertNode(insertedNode);
                }
            }
            this.contentsDeleted = true;
            return true; // Indicate we've fully handled this case.
        }
        return false; // Not fully handled, proceed to common replacement.
    };
    // Extracts child nodes of a node.
    InsertHtml.extractChildNodes = function (node) {
        var children = [];
        for (var i = 0; i < node.childNodes.length; i++) {
            children.push(node.childNodes.item(i));
        }
        return children;
    };
    // Inserts a block nodes in separate list items.
    InsertHtml.insertBlockNodesInLI = function (children, range) {
        children = this.processInsertNodes(children);
        var fragment = document.createDocumentFragment();
        for (var _i = 0, children_1 = children; _i < children_1.length; _i++) {
            var block = children_1[_i];
            var newLi = createElement('li');
            newLi.appendChild(block.cloneNode(true));
            fragment.appendChild(newLi);
        }
        this.unwrapInlineWrappers(fragment);
        var startContainerParent = range.startContainer.parentElement;
        var nextSibling = startContainerParent.nextElementSibling;
        if (range.startContainer.nodeName === 'DIV' && startContainerParent &&
            startContainerParent.nodeName === 'LI' && startContainerParent.parentNode) {
            startContainerParent.parentNode.insertBefore(fragment, nextSibling);
            detach(startContainerParent); // Detach the parent element after inserting
        }
        else {
            range.insertNode(fragment);
        }
    };
    // Processes and adjusts the child nodes before any block.
    InsertHtml.processInsertNodes = function (children) {
        var result = [];
        var inlineGroup = [];
        for (var _i = 0, children_2 = children; _i < children_2.length; _i++) {
            var child = children_2[_i];
            var isBlock = child.nodeType === Node.ELEMENT_NODE &&
                CONSTANT.BLOCK_TAGS.indexOf(child.nodeName.toLowerCase()) !== -1;
            if (isBlock) {
                if (inlineGroup.length > 0) {
                    result.push(this.wrapInlineElementsInSpan(inlineGroup));
                    inlineGroup = [];
                }
                result.push(child);
            }
            else {
                inlineGroup.push(child);
            }
        }
        if (inlineGroup.length > 0) {
            result.push(this.wrapInlineElementsInSpan(inlineGroup));
        }
        return result;
    };
    // Wraps inline elements in a span.
    InsertHtml.wrapInlineElementsInSpan = function (inlineNodes) {
        var wrapper = createElement('span');
        wrapper.className = 'inline-wrapper';
        inlineNodes.forEach(function (node) { return wrapper.appendChild(node); });
        return wrapper;
    };
    // Unwraps inline wrappers
    InsertHtml.unwrapInlineWrappers = function (root) {
        var wrappers = root.querySelectorAll('.inline-wrapper');
        wrappers.forEach(function (wrapper) {
            var parent = wrapper.parentNode;
            if (!parent) {
                return;
            }
            while (wrapper.firstChild) {
                parent.insertBefore(wrapper.firstChild, wrapper);
            }
            parent.removeChild(wrapper);
        });
    };
    // Remove empty list items after start LI
    InsertHtml.removeEmptyAfterStartLI = function (liElement, editNode) {
        var _this = this;
        this.clearIfCompletelyEmpty(liElement);
        var rootList = this.getRootList(liElement, editNode);
        if (!rootList) {
            return;
        }
        var listItems = rootList.querySelectorAll('li');
        listItems.forEach(function (item) {
            if (_this.isRemovableEmptyListItem(item, liElement)) {
                detach(item);
            }
        });
    };
    // Clear if completely empty
    InsertHtml.clearIfCompletelyEmpty = function (liElement) {
        if (liElement.textContent.length === 0 && !liElement.querySelector('audio,video,img,table,br,hr')) {
            liElement.innerHTML = '';
        }
    };
    // Get root list
    InsertHtml.getRootList = function (li, editNode) {
        var rootList = closest(li, 'ul,ol');
        while (rootList && rootList.parentElement && editNode.contains(rootList.parentElement)) {
            var parentRootList = closest(rootList.parentElement, 'ul,ol');
            if (editNode.contains(parentRootList)) {
                rootList = parentRootList;
            }
            else {
                return rootList;
            }
        }
        return rootList || null;
    };
    // Remove empty list items
    InsertHtml.isRemovableEmptyListItem = function (item, skipElement) {
        return item !== skipElement &&
            item.textContent.trim() === '' &&
            !item.querySelector('audio,video,img,table,br,hr');
    };
    // Checks if the node is a text or BR node in a list item.
    InsertHtml.isTextOrBrInListItem = function (currentNode, blockNode, editNode) {
        return currentNode &&
            ((currentNode.nodeName === '#text' || currentNode.nodeName === 'BR' || currentNode.nodeName === 'HR') &&
                !isNOU(currentNode.parentElement) &&
                (currentNode.parentElement.nodeName === 'LI' ||
                    currentNode.parentElement.closest('LI') ||
                    (blockNode === editNode && currentNode.parentElement === blockNode)) &&
                currentNode.parentElement.textContent.trim().length > 0);
    };
    // Handles insertion when the current node is text in a list item.
    InsertHtml.handleTextInListItem = function (range, insertedNode, currentNode, parentNode, nodeCutter, editNode) {
        if (currentNode.parentElement.nodeName === 'LI' &&
            !isNOU(currentNode.nextSibling) &&
            currentNode.nextSibling.nodeName === 'BR') {
            detach(currentNode.nextSibling);
        }
        var filteredChildNodes = Array.from(insertedNode.childNodes).filter(function (child) {
            return !(child.nodeName === 'LI' || child.nodeName === 'UL' || child.nodeName === 'OL');
        });
        var mergeNode = currentNode.parentElement;
        var cloneRange = null;
        var isCollapsed = range.collapsed;
        var parentLi = isCollapsed ? currentNode.parentElement.closest('LI') : null;
        var startLi = null;
        var endLi = null;
        if (!range.collapsed) {
            var startContainer = range.startContainer;
            var startOffset = range.startOffset;
            cloneRange = range.cloneRange();
            startLi = this.findLiFromContainer(cloneRange.startContainer);
            endLi = this.findLiFromContainer(cloneRange.endContainer);
            this.removeListfromPaste(range);
            if (startLi && filteredChildNodes.length > 0) {
                this.removeEmptyAfterStartLI(startLi, editNode);
            }
            range.setStart(startContainer, startOffset);
            range.setEnd(startContainer, startOffset);
        }
        var startContainerparentElement = range.startContainer.parentElement;
        var blockNode = this.getImmediateBlockNode(currentNode, insertedNode);
        if (insertedNode.firstChild.nodeName === 'HR') {
            var parentListItem = null;
            if (startLi) {
                parentListItem = closest(startLi, 'li');
            }
            else {
                parentListItem = closest(parentNode, 'li');
            }
            parentNode = parentListItem ? parentListItem : parentNode;
            this.insertBlockElementInList(range, insertedNode, parentNode, nodeCutter);
        }
        else if (isCollapsed && parentLi && filteredChildNodes.length > 0) {
            this.pasteLI(insertedNode, parentLi, mergeNode, blockNode, range, nodeCutter);
        }
        else if (!isCollapsed && startLi && endLi && filteredChildNodes.length > 0) {
            this.nonCollapsedInsertion(insertedNode, cloneRange, nodeCutter, endLi);
        }
        else if (isCollapsed && ((insertedNode.firstChild.nodeName === 'UL' ||
            insertedNode.firstChild.nodeName === 'OL') && currentNode.parentElement && currentNode.parentElement.nodeName === 'DIV')) {
            // Case 1: If cursor is at the start of the list item
            if (range.startOffset === 0 && range.endOffset === 0 && startContainerparentElement) {
                startContainerparentElement.parentNode.insertBefore(insertedNode, startContainerparentElement);
            }
            // Case 2: If cursor is at the end of the list item
            else if (range.startOffset === range.startContainer.textContent.length && range.endOffset ===
                range.startContainer.textContent.length &&
                startContainerparentElement && startContainerparentElement.parentNode) {
                var nextSibling = startContainerparentElement.nextSibling;
                startContainerparentElement.parentNode.insertBefore(insertedNode, nextSibling);
            }
            else {
                var liElement = startContainerparentElement.closest('li');
                // Use nodeCutter to split the node at the range
                var spliceNode = nodeCutter.GetSpliceNode(range, startContainerparentElement);
                // Create a new <li> to hold the remaining content after the splice
                var newLi = document.createElement('li');
                // Move all nodes after spliceNode into newLi
                var nextNode = spliceNode.nextSibling;
                newLi.appendChild(nextNode);
                // Insert the pasted list items (insertedNode) after the current <li>
                insertedNode.appendChild(newLi);
                liElement.parentNode.insertBefore(insertedNode, liElement.nextSibling);
            }
        }
        else if (!isCollapsed && ((range.startContainer.nodeName === '#text' && startContainerparentElement.nodeName === 'DIV') ||
            (range.startContainer.nodeName === 'DIV' && startContainerparentElement.nodeName === 'LI'))) {
            var startContainer = (range.startContainer.nodeName === '#text' && range.startContainer.textContent.trim().length === 0) ?
                startContainerparentElement : range.startContainer;
            if (startContainer && startContainer.parentElement.nodeName === 'LI') {
                startContainerparentElement.parentNode.insertBefore(insertedNode, startContainerparentElement.nextSibling);
            }
        }
        else {
            range.insertNode(insertedNode);
        }
        this.contentsDeleted = true;
        return true; // Indicate we've fully handled this case.
    };
    // Returns a LI node from any container
    InsertHtml.findLiFromContainer = function (container) {
        if (container.nodeName === 'LI') {
            return container;
        }
        var parent = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
        parent = parent.nodeName === 'LI' ? parent : parent.closest('LI');
        return parent;
    };
    //Handles non-collapsed list insertion logic for splitting and merging list items based on selection range.
    InsertHtml.nonCollapsedInsertion = function (insertedNode, cloneRange, nodeCutter, endSelectionLi) {
        var children = this.extractChildNodes(insertedNode);
        children = this.processInsertNodes(children);
        var startContainer = cloneRange.startContainer;
        var endContainer = cloneRange.endContainer;
        var isEndContainerLi = endContainer.nodeName === 'UL' || endContainer.nodeName === 'OL';
        var parentLi = this.getClosestLi(startContainer);
        var previousLi = this.getPreviousLi(parentLi);
        var endLi = this.getNextLi(parentLi);
        var parentList = parentLi.parentNode;
        if (endLi && parentList === endContainer) {
            if (isEndContainerLi && endSelectionLi.textContent === '') {
                endLi = null;
            }
        }
        if (startContainer === endContainer || (!endLi || (parentLi.contains(endContainer) && !isEndContainerLi)) &&
            !this.isAnotherLiFromEndLi || this.isAnotherLiFromEndLi && parentList !== endContainer && endContainer.nodeName !== 'A') {
            this.handleSingleLiInsertion(parentLi, previousLi, endLi, children, startContainer, cloneRange, nodeCutter, parentList);
        }
        else {
            this.handleMultiLiInsertion(parentLi, children, startContainer, endContainer, parentList);
        }
        this.unwrapInlineWrappers(parentList);
    };
    // Returns the nearest ancestor LI element for a given node
    InsertHtml.getClosestLi = function (node) {
        var current = node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
        while (current && current.nodeName !== 'LI') {
            current = current.parentNode;
        }
        return current;
    };
    // Returns the previous LI sibling if available
    InsertHtml.getPreviousLi = function (li) {
        var prev = li.previousSibling;
        return (prev && prev.nodeName === 'LI') ? prev : null;
    };
    // Returns the next LI sibling if available
    InsertHtml.getNextLi = function (li) {
        var next = li.nextSibling;
        return (next && next.nodeName === 'LI') ? next : null;
    };
    // Appends list items to a fragment and returns the last appended list item
    InsertHtml.appendListItems = function (fragment, children, startIndex, endIndex) {
        var lastNewLi = null;
        for (var i = startIndex; i < endIndex; i++) {
            var li = document.createElement('li');
            li.appendChild(children[i]);
            fragment.appendChild(li);
            lastNewLi = li;
        }
        return lastNewLi;
    };
    // Handles insertion when start and end container are in different LIs
    InsertHtml.moveSiblingsToLiAndInsert = function (fromNode, targetLi, fragment, parentLi, parentList) {
        var elementsToMove = [];
        while (fromNode) {
            elementsToMove.push(fromNode);
            fromNode = fromNode.nextSibling;
        }
        for (var i = 0; i < elementsToMove.length; i++) {
            if (parentLi.contains(elementsToMove[i])) {
                parentLi.removeChild(elementsToMove[i]);
            }
        }
        for (var i = 0; i < elementsToMove.length; i++) {
            targetLi.appendChild(elementsToMove[i]);
        }
        if (parentLi.nextSibling) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
        }
        else {
            parentLi.appendChild(fragment);
        }
    };
    // Handles insertion when start and end container are in same LI or no end LI
    InsertHtml.handleSingleLiInsertion = function (parentLi, previousLi, endLi, children, startContainer, cloneRange, nodeCutter, parentList) {
        var fragment = document.createDocumentFragment();
        this.extractNestedListsIntoNewListItem(parentLi);
        var middleLi = null;
        var lastNode = null;
        var preNode = parentLi.hasChildNodes() &&
            (parentLi.lastChild.nodeType === Node.TEXT_NODE || parentLi.textContent === '')
            ? parentLi : parentLi.lastChild;
        if (startContainer && startContainer.textContent && startContainer.textContent.length > 0) {
            middleLi = nodeCutter.GetSpliceNode(cloneRange, startContainer);
            preNode = middleLi.previousSibling !== previousLi ? middleLi.previousSibling : null;
            lastNode = middleLi.nextSibling !== endLi ? middleLi.nextSibling : null;
        }
        var firstBlock = children[0];
        var isSingleBlock = children.length === 1;
        if (isSingleBlock) {
            if (lastNode) {
                this.addCursorMarker(lastNode);
                this.moveAllChildren(lastNode, firstBlock);
                lastNode.parentNode.removeChild(lastNode);
            }
            else {
                this.addCursorMarker(firstBlock, true);
            }
        }
        if (preNode && preNode !== previousLi && preNode.textContent && preNode.textContent.length > 0) {
            this.moveAllChildren(firstBlock, preNode);
        }
        else if (isSingleBlock && parentLi.textContent === '') {
            parentLi.appendChild(firstBlock);
        }
        else {
            var newLi = createElement('li');
            newLi.appendChild(firstBlock);
            fragment.appendChild(newLi);
        }
        var lastNewLi = this.appendListItems(fragment, children, 1, children.length);
        if (lastNewLi && lastNode) {
            this.addCursorMarker(lastNode);
            if (lastNode.nodeName === 'A') {
                lastNewLi.lastChild.appendChild(lastNode);
            }
            else {
                this.mergeLastNodeContent(lastNode, lastNewLi);
            }
        }
        var shouldInsertAfter = lastNode && (lastNode.nodeName === 'LI' || !lastNode.nextSibling);
        if (shouldInsertAfter) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
            if (lastNode && lastNode.parentNode && lastNode.textContent.length === 0) {
                lastNode.parentNode.removeChild(lastNode);
            }
        }
        else if (lastNewLi) {
            this.moveSiblingsToLiAndInsert(lastNode, lastNewLi, fragment, parentLi, parentList);
        }
        if (middleLi && middleLi.parentNode && middleLi.textContent === '') {
            middleLi.parentNode.removeChild(middleLi);
        }
        if (parentLi && parentLi.parentNode && parentLi.textContent === '') {
            parentLi.parentNode.removeChild(parentLi);
        }
    };
    // Handles insertion when selection spans multiple LIs
    InsertHtml.handleMultiLiInsertion = function (parentLi, children, startContainer, endContainer, parentList) {
        var fragment = document.createDocumentFragment();
        this.extractNestedListsIntoNewListItem(parentLi);
        var endLi = parentLi.nextSibling;
        if (endLi) {
            this.extractNestedListsIntoNewListItem(endLi);
        }
        startContainer = startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentNode : startContainer;
        if (endContainer.textContent === '' && endContainer.nextSibling) {
            endContainer = endContainer.nextSibling;
        }
        if (!endLi.contains(endContainer) || endContainer.nodeName === 'UL' || endContainer.nodeName === 'OL') {
            endContainer = endLi;
        }
        var firstBlock = children[0];
        var lastBlock = children[children.length - 1];
        if (endContainer.nodeType === Node.TEXT_NODE && children.length > 1) {
            lastBlock.appendChild(endContainer);
        }
        else if (children.length > 1) {
            this.addCursorMarker(endContainer);
            this.moveAllChildren(endContainer, lastBlock);
            endLi.insertBefore(lastBlock, endLi.firstChild);
        }
        if (children.length === 1) {
            this.addCursorMarker(endContainer);
            this.moveAllChildren(endContainer, firstBlock);
            if (endLi && endLi.parentNode) {
                endLi.parentNode.removeChild(endLi);
            }
        }
        var lastNewLi = null;
        if (startContainer.textContent.length > 0 && parentLi.textContent.length > 0) {
            this.moveAllChildren(firstBlock, startContainer);
            if (startContainer.nodeName === 'A') {
                startContainer = startContainer.parentNode.lastChild;
            }
        }
        else {
            var newLi = createElement('li');
            newLi.appendChild(firstBlock);
            fragment.appendChild(newLi);
            if (children.length === 1) {
                lastNewLi = newLi;
            }
        }
        if (isNOU(lastNewLi)) {
            lastNewLi = this.appendListItems(fragment, children, 1, children.length - 1);
        }
        if (isNOU(startContainer.nextSibling)) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
        }
        else if (lastNewLi) {
            this.moveSiblingsToLiAndInsert(startContainer.nextSibling, lastNewLi, fragment, parentLi, parentList);
        }
        else {
            // nextSibling exists → insert before it
            parentList.insertBefore(fragment, startContainer.nextSibling);
        }
        if (parentLi.textContent === '' && parentLi.parentNode) {
            parentLi.parentNode.removeChild(parentLi);
        }
    };
    // Handles insertion for collapsed selection
    InsertHtml.pasteLI = function (insertedNode, parentLi, mergeNode, blockNode, range, nodeCutter) {
        var children = this.extractChildNodes(insertedNode);
        children = this.processInsertNodes(children);
        var blockNodeLength = this.getBlockNodeLength(blockNode);
        var parentList = parentLi.parentNode;
        var isCursorAtStart = true;
        var isCursorAtEnd = false;
        if (parentLi.contains(mergeNode) && mergeNode.previousSibling && mergeNode.previousSibling.textContent.trim().length !== 0) {
            isCursorAtStart = false;
        }
        var isAtStart = range.startOffset === 0 && isCursorAtStart;
        if (!isAtStart) {
            var parentLiLastChild = parentLi.lastChild;
            while (!isNOU(parentLiLastChild) && parentLiLastChild.nodeType === Node.ELEMENT_NODE &&
                !this.isBlockElement(parentLiLastChild) && !isNOU(parentLiLastChild.lastChild)) {
                parentLiLastChild = parentLiLastChild.lastChild;
            }
            if (range.startContainer === parentLiLastChild &&
                range.startContainer.textContent.length === range.startOffset) {
                isCursorAtEnd = true;
            }
            else if (parentLi.contains(mergeNode) && (isNOU(mergeNode.nextSibling) || mergeNode.nextSibling && ['LI', 'UL', 'OL'].indexOf(mergeNode.nextSibling.nodeName) !== -1) && range.startOffset === mergeNode.textContent.length) {
                var previousSib = mergeNode.previousSibling;
                var textLength = range.startOffset;
                while (previousSib && previousSib.nodeName !== 'LI') {
                    textLength += previousSib.textContent.length;
                    previousSib = previousSib.previousSibling;
                }
                isCursorAtEnd = textLength === blockNodeLength;
            }
        }
        var isAtEnd = range.startOffset === blockNodeLength || isCursorAtEnd;
        if (isAtStart) {
            this.handlePasteAtStart(children, parentLi, mergeNode, parentList);
        }
        else if (isAtEnd) {
            this.handlePasteAtEnd(children, parentLi, mergeNode, parentList);
        }
        else {
            this.handlePasteInMiddle(children, parentLi, mergeNode, range, parentList, nodeCutter);
        }
        this.unwrapInlineWrappers(parentList);
    };
    // Handles insertion at start
    InsertHtml.handlePasteAtStart = function (children, parentLi, mergeNode, parentList) {
        var lastBlock = children[children.length - 1];
        this.addCursorMarker(mergeNode);
        this.moveAllChildren(mergeNode, lastBlock);
        parentLi.insertBefore(lastBlock, parentLi.firstChild);
        var fragment = this.createLiFragment(children, 0, children.length - 1); // exclude last
        parentList.insertBefore(fragment, parentLi);
    };
    // Handles insertion at end
    InsertHtml.handlePasteAtEnd = function (children, parentLi, mergeNode, parentList) {
        var firstBlock = children[0];
        var hasNestedList = this.hasNestedListInsideLi(mergeNode);
        if (mergeNode.nodeName === 'LI' && hasNestedList) {
            var movedNodes = this.collectAndRemoveFollowingNodes(parentLi, hasNestedList);
            this.moveAllChildren(firstBlock, mergeNode);
            movedNodes.forEach(function (node) { return mergeNode.appendChild(node); });
        }
        else {
            this.moveAllChildren(firstBlock, mergeNode);
        }
        var fragment = this.createLiFragment(children, 1, children.length); // exclude first
        var lastNewLi = fragment.lastChild;
        var cursorNode = parentLi;
        if (isNOU(lastNewLi)) {
            cursorNode = parentLi;
        }
        else {
            cursorNode = lastNewLi;
        }
        while (!isNOU(cursorNode.lastChild) && cursorNode.lastChild.nodeName !== '#text') {
            cursorNode = cursorNode.lastChild;
        }
        this.addCursorMarker(cursorNode, true);
        if (lastNewLi) {
            var movedNodes = this.collectAndRemoveFollowingNodes(parentLi, hasNestedList ? hasNestedList : mergeNode.nextSibling);
            movedNodes.forEach(function (node) { return lastNewLi.appendChild(node); });
            this.insertFragmentAfterLi(fragment, parentLi, parentList);
        }
    };
    // Handles insertion in middle
    InsertHtml.handlePasteInMiddle = function (children, parentLi, mergeNode, range, parentList, nodeCutter) {
        var middleLi = nodeCutter.GetSpliceNode(range, mergeNode);
        var preNode = middleLi.previousSibling;
        var lastNode = middleLi.nextSibling;
        var firstBlock = children[0];
        if (children.length === 1) {
            this.addCursorMarker(lastNode);
            this.moveAllChildren(lastNode, firstBlock);
        }
        this.moveAllChildren(firstBlock, preNode);
        var fragment = this.createLiFragment(children, 1, children.length); // exclude first
        var lastNewLi = fragment.lastChild;
        if (lastNewLi) {
            this.addCursorMarker(lastNode);
            if (lastNode.nodeName === 'A') {
                lastNewLi.lastChild.appendChild(lastNode);
            }
            else {
                this.mergeLastNodeContent(lastNode, lastNewLi);
            }
        }
        var hasNestedList = this.hasNestedListInsideLi(parentLi);
        if ((lastNode && isNOU(lastNode.nextSibling) && lastNewLi && isNOU(hasNestedList)) || lastNode.nodeName === 'LI') {
            parentList.insertBefore(fragment, parentLi.nextSibling);
            if (lastNode.textContent.length === 0) {
                lastNode.parentNode.removeChild(lastNode);
            }
        }
        else if (lastNewLi) {
            var movedNodes = this.collectAndRemoveFollowingNodes(parentLi, hasNestedList ? hasNestedList : lastNode);
            movedNodes.forEach(function (node) { return lastNewLi.appendChild(node); });
            this.insertFragmentAfterLi(fragment, parentLi, parentList);
        }
        middleLi.parentNode.removeChild(middleLi);
    };
    // Checks if there is any nested list inside li
    InsertHtml.hasNestedListInsideLi = function (node) {
        if (node.nodeName === 'LI') {
            for (var _i = 0, _a = Array.from(node.children); _i < _a.length; _i++) {
                var child = _a[_i];
                if (child.tagName === 'UL' || child.tagName === 'OL') {
                    return child;
                }
            }
        }
        var closestLi = node.closest('LI');
        if (!closestLi) {
            return null;
        }
        for (var _b = 0, _c = Array.from(closestLi.children); _b < _c.length; _b++) {
            var child = _c[_b];
            if (child.tagName === 'UL' || child.tagName === 'OL') {
                return child;
            }
        }
        return null;
    };
    // Returns the length of block node
    InsertHtml.getBlockNodeLength = function (blockNode) {
        if (blockNode.nodeName === 'LI') {
            var length_1 = 0;
            for (var _i = 0, _a = Array.from(blockNode.childNodes); _i < _a.length; _i++) {
                var child = _a[_i];
                if (child.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].indexOf(child.tagName) !== -1) {
                    break;
                }
                length_1 += child.textContent ? child.textContent.length : 0;
            }
            return length_1;
        }
        return blockNode.textContent ? blockNode.textContent.length : 0;
    };
    // Adds cursor marker
    InsertHtml.addCursorMarker = function (lastNode, isEnd) {
        var span = createElement('span');
        span.className = 'paste-cursor';
        if (isEnd) {
            lastNode.appendChild(span);
        }
        else {
            lastNode.insertBefore(span, lastNode.firstChild);
        }
    };
    // Checks if list item has another list
    InsertHtml.extractNestedListsIntoNewListItem = function (listItem) {
        var childNodes = Array.from(listItem.childNodes);
        var listNodes = [];
        // Find ul/ol nodes
        for (var _i = 0, childNodes_1 = childNodes; _i < childNodes_1.length; _i++) {
            var node = childNodes_1[_i];
            if (node.nodeType === Node.ELEMENT_NODE &&
                (node.tagName === 'UL' || node.tagName === 'OL')) {
                listNodes.push(node);
            }
        }
        if (listNodes.length > 0) {
            // Create a new <li>
            var newLi = createElement('li');
            // Move ul/ol into the new <li>
            for (var _a = 0, listNodes_1 = listNodes; _a < listNodes_1.length; _a++) {
                var list = listNodes_1[_a];
                newLi.appendChild(list);
            }
            // Insert new <li> after mergeNode
            var parent_1 = listItem.parentNode;
            if (parent_1) {
                var next = listItem.nextSibling;
                if (next) {
                    parent_1.insertBefore(newLi, next);
                }
                else {
                    parent_1.appendChild(newLi);
                }
            }
        }
    };
    // Creates a fragment of list items
    InsertHtml.createLiFragment = function (nodes, start, end) {
        var fragment = document.createDocumentFragment();
        if (nodes.length === 2 && start === 1 &&
            (nodes[1].nodeName === 'UL' || nodes[1].nodeName === 'OL')) {
            // execute this code only for nested list paste use case.
            fragment.appendChild(nodes[1]);
        }
        else {
            for (var i = start; i < end; i++) {
                var node = nodes[i];
                if (node.nodeName === 'UL' || node.nodeName === 'OL') {
                    // snapshot childNodes to avoid live-collection mutation
                    var children = Array.from(node.childNodes);
                    for (var _i = 0, children_3 = children; _i < children_3.length; _i++) {
                        var child = children_3[_i];
                        if (this.isBlockElement(child)) {
                            fragment.appendChild(child.cloneNode(true));
                        }
                    }
                }
                else {
                    var li = createElement('li');
                    li.appendChild(nodes[i]);
                    fragment.appendChild(li);
                }
            }
        }
        return fragment;
    };
    // Collects and removes following nodes
    InsertHtml.collectAndRemoveFollowingNodes = function (parentLi, startNode) {
        var nodes = [];
        var current = startNode;
        while (current) {
            var next = current.nextSibling;
            nodes.push(current);
            if (parentLi.contains(current)) {
                parentLi.removeChild(current);
            }
            current = next;
        }
        return nodes;
    };
    // Inserts fragment after list item
    InsertHtml.insertFragmentAfterLi = function (fragment, parentLi, parentList) {
        if (parentLi.nextSibling) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
        }
        else {
            parentLi.appendChild(fragment);
        }
    };
    // Moves all children
    InsertHtml.moveAllChildren = function (sourceNode, targetNode) {
        var isAnchorInTargetNode = targetNode.nodeName === 'A';
        var isAnchorInSourceNode = sourceNode.nodeName === 'A';
        while (sourceNode.firstChild && !isAnchorInSourceNode) {
            var firstChild = sourceNode.firstChild;
            if (firstChild.nodeName === 'UL' || firstChild.nodeName === 'OL') {
                return;
            }
            if (isAnchorInTargetNode) {
                targetNode.parentNode.insertBefore(firstChild, targetNode.nextSibling);
                targetNode = targetNode.nextSibling;
            }
            else {
                targetNode.appendChild(firstChild);
            }
        }
        if (isAnchorInSourceNode) {
            targetNode.appendChild(sourceNode);
        }
    };
    // Merges last node content
    InsertHtml.mergeLastNodeContent = function (lastNode, lastNewLi) {
        while (lastNode && lastNode.firstChild) {
            var firstChild = lastNode.firstChild;
            if (!firstChild) {
                continue;
            }
            var isBlockTag = CONSTANT.BLOCK_TAGS.indexOf(firstChild.nodeName.toLowerCase()) >= 0;
            if (!isBlockTag && lastNewLi.lastChild && lastNewLi.lastChild.nodeType === Node.ELEMENT_NODE) {
                lastNewLi.lastChild.appendChild(firstChild);
            }
            else if (firstChild.nodeName === 'UL' || firstChild.nodeName === 'OL') {
                lastNewLi.appendChild(firstChild);
            }
            else {
                this.moveAllChildren(firstChild, lastNewLi.lastChild);
                lastNode.removeChild(firstChild);
            }
        }
    };
    // Gets the appropriate node splice element based on selection and context.
    InsertHtml.getSplitElementForInsertion = function (range, nodeCutter, blockNode) {
        var isSelectionCollapsed = range.collapsed;
        var isAtNodeStart = range.startOffset === 0;
        var isAtNodeEnd = range.startContainer.nodeType === Node.TEXT_NODE ?
            range.startOffset === range.startContainer.textContent.length :
            range.startOffset === range.startContainer.childNodes.length;
        if (blockNode &&
            blockNode.nodeName === 'P' &&
            (isAtNodeStart || isAtNodeEnd || !isSelectionCollapsed) &&
            blockNode.textContent.trim() === '') {
            // Use a single split for empty paragraphs or paragraphs with only cursor position.
            return nodeCutter.SplitNode(range, blockNode, true);
        }
        else {
            // Use full GetSpliceNode for other cases.
            return nodeCutter.GetSpliceNode(range, blockNode);
        }
    };
    // Adjusts the cursor position post-insertion to ensure it is placed at the correct point.
    InsertHtml.cursorPos = function (lastSelectionNode, insertedNode, nodeSelection, docElement, editNode) {
        lastSelectionNode.classList.add('lastNode');
        editNode.innerHTML = updateTextNode(editNode.innerHTML, this.isBlazor);
        lastSelectionNode = editNode.querySelector('.lastNode');
        if (!isNOU(lastSelectionNode)) {
            this.placeCursorEnd(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode);
            lastSelectionNode.classList.remove('lastNode');
            if (lastSelectionNode.classList.length === 0) {
                lastSelectionNode.removeAttribute('class');
            }
        }
    };
    // Handles focus management specifically for image/audio/video elements during insertion operations.
    InsertHtml.mediaFocus = function (node, nodeSelection, docElement) {
        var focusNode = document.createTextNode('\u00A0');
        var wrapper = node.nodeType === Node.ELEMENT_NODE ? node.closest('.e-audio-wrap, .e-video-wrap') : null;
        if (node.parentNode && node.parentNode.nodeName === 'A') {
            var anchorTag = node.parentNode;
            var parentNode = anchorTag.parentNode;
            parentNode.insertBefore(focusNode, anchorTag.nextSibling);
            parentNode.insertBefore(node, focusNode);
        }
        else if (node.nodeName === 'IMG' && (!node.nextSibling ||
            (node.nextSibling && node.nextSibling.nodeName === '#text' && node.nextSibling.textContent !== '\u00A0'))) {
            node.parentNode.insertBefore(focusNode, node.nextSibling);
        }
        else if (!isNOU(wrapper) && (node.nodeName === 'VIDEO' || node.nodeName === 'AUDIO')) {
            wrapper.parentNode.insertBefore(focusNode, wrapper.nextSibling);
            nodeSelection.setSelectionText(docElement, focusNode, focusNode, 0, 0);
            return;
        }
        nodeSelection.setSelectionText(docElement, node.nextSibling, node.nextSibling, 0, 0);
    };
    // Identifies the immediate block-level node, utilized for placement and alignment logic.
    // eslint-disable-next-line
    InsertHtml.getImmediateBlockNode = function (node, editNode) {
        while (node && CONSTANT.BLOCK_TAGS.indexOf(node.nodeName.toLocaleLowerCase()) < 0) {
            node = node.parentNode;
        }
        return node;
    };
    // Eliminates comments from a node to ensure the insertion is clean and comment-free.
    InsertHtml.removingComments = function (insertedNode) {
        var innerElement = insertedNode.innerHTML;
        innerElement = innerElement.replace(/<!--[\s\S]*?-->/g, '');
        insertedNode.innerHTML = innerElement;
    };
    // Finds and detaches empty elements from the DOM.
    InsertHtml.findDetachEmptyElem = function (element, ignoreBlockNodes) {
        if (ignoreBlockNodes === void 0) { ignoreBlockNodes = false; }
        var removableElement;
        if (!isNOU(element.parentElement)) {
            var hasNbsp = element.parentElement.textContent.length > 0 && element.parentElement.textContent.match(/\u00a0/g)
                && element.parentElement.textContent.match(/\u00a0/g).length > 0;
            var hasBr = !isNOU(element.parentElement.querySelector('br'));
            if (!hasNbsp && !hasBr && element.parentElement.textContent.trim() === '' && element.parentElement.contentEditable !== 'true' &&
                isNOU(element.parentElement.querySelector('img')) && element.parentElement.nodeName !== 'TD' && element.parentElement.nodeName !== 'TH' && isNOU(element.parentElement.querySelector('table td, table th'))) {
                removableElement = ignoreBlockNodes && CONSTANT.BLOCK_TAGS.indexOf(element.parentElement.tagName.toLowerCase()) !== -1 ?
                    element : this.findDetachEmptyElem(element.parentElement, ignoreBlockNodes);
            }
            else {
                removableElement = ignoreBlockNodes && CONSTANT.BLOCK_TAGS.indexOf(element.tagName.toLowerCase()) !== -1 ? null :
                    element;
            }
        }
        else {
            removableElement = null;
        }
        return removableElement;
    };
    // Removes elements deemed empty if isolated.
    InsertHtml.removeEmptyElements = function (element, ignoreBlockNodes, emptyElemet) {
        if (ignoreBlockNodes === void 0) { ignoreBlockNodes = false; }
        if (emptyElemet === void 0) { emptyElemet = null; }
        var emptyElements = element.querySelectorAll(':empty');
        var filteredEmptyElements = Array.from(emptyElements).filter(function (element) {
            var tagName = element.tagName.toLowerCase();
            // Some empty tags suc as TD TH convey a meaning and hence should not be removed.
            var meaningfulEmptyTags = ['td', 'tr', 'th', 'textarea', 'input', 'img', 'video', 'audio', 'br', 'hr', 'iframe'];
            return !element.closest('svg') && !element.closest('canvas') && !(meaningfulEmptyTags.indexOf(tagName) > -1);
        });
        for (var i = 0; i < filteredEmptyElements.length; i++) {
            var lineWithDiv = true;
            var currentEmptyElem = filteredEmptyElements[i];
            if (currentEmptyElem.tagName === 'DIV') {
                lineWithDiv = currentEmptyElem.style.borderBottom === 'none' ||
                    currentEmptyElem.style.borderBottom === '' ? true : false;
            }
            if (currentEmptyElem.nodeName === 'COL') {
                if (!currentEmptyElem.style.width) {
                    var colGroup = currentEmptyElem.parentElement;
                    detach(colGroup);
                }
                continue;
            }
            var isEmptyElement = !isNOU(emptyElemet) && currentEmptyElem === emptyElemet;
            if (CONSTANT.SELF_CLOSING_TAGS.indexOf(currentEmptyElem.tagName.toLowerCase()) < 0 && lineWithDiv && !isEmptyElement) {
                var detachableElement = this.findDetachEmptyElem(currentEmptyElem, ignoreBlockNodes);
                if (!isNOU(detachableElement) && !(detachableElement.nodeType === Node.ELEMENT_NODE && detachableElement.nodeName.toUpperCase() === 'TEXTAREA')) {
                    detach(detachableElement);
                }
            }
        }
    };
    // Finds the most relevant parent element considered in operations like insertion or cleanup.
    InsertHtml.findClosestRelevantElement = function (sourceElement, editNode) {
        // Cast to Element type for proper handling.
        var currentElement = sourceElement;
        // First check if the element is inside a table or list item.
        var relevantAncestorTags = ['table', 'li'];
        for (var _i = 0, relevantAncestorTags_1 = relevantAncestorTags; _i < relevantAncestorTags_1.length; _i++) {
            var ancestorTag = relevantAncestorTags_1[_i];
            var closestAncestorElement = closest(currentElement, ancestorTag);
            if (closestAncestorElement && !closestAncestorElement.contains(editNode)) {
                return closestAncestorElement;
            }
        }
        // Traverse up the DOM tree until we reach a valid parent or run out of elements.
        while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
            var parentElement = currentElement.parentNode;
            if (parentElement === editNode) {
                return currentElement;
            }
            // Check if parent is one of the allowed block elements.
            var isParentTagValid = !isNOU(parentElement.tagName) && (this.isTagInList(parentElement.tagName, CONSTANT.IGNORE_BLOCK_TAGS) ||
                this.isTagInList(parentElement.tagName, CONSTANT.ALLOWED_TABLE_BLOCK_TAGS));
            if (isParentTagValid) {
                return currentElement;
            }
            // Move up to the parent element.
            currentElement = parentElement;
        }
        return null;
    };
    // Determines if a provided tag matches any entries in a given list of permissible tags.
    InsertHtml.isTagInList = function (tagName, tagList) {
        return tagList.indexOf(tagName.toLowerCase()) !== -1;
    };
    // Facilitates the insertion of a table within a list structure, reorganizing elements as needed.
    InsertHtml.insertTableInList = function (range, insertNode, parentNode, currentNode, nodeCutter, lastclosestParentNode) {
        var parentList = closest(parentNode, 'ul,ol');
        var totalLi = parentList ? parentList.querySelectorAll('li').length : 0;
        var preNode = nodeCutter.SplitNode(range, parentNode, true);
        var sibNode = preNode.previousElementSibling;
        // Get next sibling info for potential content movement.
        var nextSibNode = lastclosestParentNode ? closest(lastclosestParentNode, 'li') : null;
        var nextSibNodeInitialHTML = nextSibNode ? nextSibNode.innerHTML : null;
        // Determine if we have a valid previous sibling in a list with more items than the original.
        var hasSiblingInLargerList = sibNode && closest(sibNode, 'ol,ul') &&
            closest(sibNode, 'ol,ul').querySelectorAll('li').length > totalLi;
        if (hasSiblingInLargerList) {
            // Insert table inside previous sibling and move content there.
            sibNode.appendChild(insertNode);
            range.deleteContents();
            // Move content from preNode to sibNode if needed.
            if (preNode.childNodes.length > 0) {
                this.moveChildNodes(preNode, sibNode);
            }
            // Handle content movement from next sibling if necessary.
            var nextSiblingContentChanged = parentNode !== lastclosestParentNode &&
                nextSibNodeInitialHTML && nextSibNodeInitialHTML !== nextSibNode.innerHTML;
            if (nextSiblingContentChanged) {
                this.moveChildNodes(nextSibNode, sibNode);
            }
        }
        else {
            // Insert table at beginning of current node.
            range.deleteContents();
            preNode.insertBefore(insertNode, preNode.firstChild);
            // Move content if needed.
            if (parentNode !== lastclosestParentNode) {
                this.moveChildNodes(lastclosestParentNode, parentNode);
            }
        }
        // Clean up and mark table.
        this.removeEmptyNextLI(closest(insertNode, 'li'));
        insertNode.classList.add('ignore-table');
    };
    // Transfers child nodes from a source to target element to symmetrically manage content.
    InsertHtml.moveChildNodes = function (source, target) {
        while (!isNOU(source) && !isNOU(source.firstChild)) {
            target.appendChild(source.firstChild);
        }
    };
    // Checks and adjusts text alignment in elements affected by new content insertions.
    InsertHtml.alignCheck = function (editNode) {
        var spanAligns = editNode.querySelectorAll('span[style*="text-align"]');
        for (var i = 0; i < spanAligns.length; i++) {
            var spanAlign = spanAligns[i];
            if (spanAlign) {
                var blockAlign = this.getImmediateBlockNode(spanAlign, null);
                if (blockAlign) {
                    var totalSpanText = '';
                    for (var j = 0; j < spanAligns.length; j++) {
                        var span = spanAligns[j];
                        if (blockAlign.contains(span)) {
                            totalSpanText += span.textContent;
                        }
                    }
                    if (blockAlign.textContent.trim() === totalSpanText.trim()) {
                        blockAlign.style.textAlign = spanAlign.style.textAlign;
                    }
                }
            }
        }
    };
    // Removes list structures from the pasted content, cleaning up unnecessary list items.
    InsertHtml.removeListfromPaste = function (range) {
        range.deleteContents();
        var value = range.startContainer;
        if (!isNOU(value) && value.nodeName === 'LI' && !isNOU(value.parentElement) && (value.parentElement.nodeName === 'OL' || value.parentElement.nodeName === 'UL') && value.textContent.trim() === '') {
            value.parentElement.querySelectorAll('li').forEach(function (item) {
                if (item.textContent.trim() === '' && item !== value) {
                    item.remove();
                }
            });
        }
    };
    // Check if we're inserting a horizontal rule in an empty block element
    InsertHtml.isHorizontalRuleInEmptyBlock = function (node, range) {
        if (node.nodeName !== 'HR') {
            return null;
        }
        var container = range.startContainer;
        while (!this.isBlockElement(container) && !isNOU(container.parentElement)) {
            container = container.parentElement;
        }
        var isTextContentPresent = container.textContent.trim() !== '';
        var isMediaElementPresent = !isNOU(container.querySelector('video')) ||
            !isNOU(container.querySelector('audio')) || !isNOU(container.querySelector('img'));
        return !(isTextContentPresent || isMediaElementPresent) ? container : null;
    };
    // Check if the node is a media element
    InsertHtml.isMediaElement = function (node) {
        if (node) {
            return node.nodeName === 'VIDEO' || node.nodeName === 'AUDIO';
        }
        return false;
    };
    // Method to insert block elements correctly in a list structure
    InsertHtml.insertBlockElementInList = function (range, insertNode, parentNode, nodeCutter) {
        var parentList = closest(parentNode, 'ul,ol');
        var totalListItems = parentList ? parentList.querySelectorAll('li').length : 0;
        var newListNode = nodeCutter.SplitNode(range, parentNode, true);
        var currentListNode = newListNode.previousElementSibling;
        // Insert the block element before the list or inside it based on context
        if (currentListNode && parentList && parentList.querySelectorAll('li').length > totalListItems) {
            currentListNode.appendChild(insertNode.firstChild);
            if (newListNode.childNodes.length > 0) {
                this.moveChildNodes(newListNode, currentListNode);
            }
        }
        else {
            if (newListNode.firstChild && newListNode.firstChild.nodeName === 'HR') {
                newListNode.insertBefore(insertNode.firstChild, newListNode.firstChild.nextSibling);
            }
            else {
                newListNode.insertBefore(insertNode.firstChild, newListNode.firstChild);
            }
        }
        // Cleanup and ensure the block element is set correctly
        if (newListNode.textContent.trim() === '' && newListNode.childNodes.length < 1) {
            detach(newListNode);
        }
    };
    InsertHtml.inlineNode = ['a', 'abbr', 'acronym', 'audio', 'b', 'bdi', 'bdo', 'big', 'br', 'button',
        'canvas', 'cite', 'code', 'data', 'datalist', 'del', 'dfn', 'em', 'embed', 'font', 'i', 'iframe', 'img', 'input',
        'ins', 'kbd', 'label', 'map', 'mark', 'meter', 'noscript', 'object', 'output', 'picture', 'progress',
        'q', 'ruby', 's', 'samp', 'script', 'select', 'slot', 'small', 'span', 'strong', 'sub', 'sup', 'svg',
        'template', 'textarea', 'time', 'title', 'u', 'tt', 'var', 'video', 'wbr'];
    InsertHtml.contentsDeleted = false;
    InsertHtml.isBlazor = false;
    InsertHtml.isAnotherLiFromEndLi = false;
    return InsertHtml;
}());
export { InsertHtml };
